import type { Messages } from './en-US';

/**
 * Latin American Spanish (es-419) translation bundle. Typed as
 * {@link Messages} so any key added in `en-US.ts` becomes a compile-time
 * error here until translated.
 */
export const esMessages: Messages = {
  common: {
    languageUpdateFailed: 'No se pudo actualizar el idioma',
    unknownPatient: 'Paciente desconocido',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    somethingWentWrong: 'Algo salió mal. Por favor, intenta de nuevo.',
    copyToClipboard: 'Copiar al portapapeles',
    copyWithText: 'Copiar: {text}',
    copied: '¡Copiado!',
    newSession: 'Nueva sesión',
  },
  widgetHeader: {
    settings: 'Configuración',
    accountSettings: 'Configuración de la cuenta',
    customizeInsights: 'Personalizar Insights',
    helpAndFeedback: 'Ayuda y comentarios',
    openInEluve: 'Abrir en Eluve',
    hide: 'Minimizar',
    testSentry: 'Test Sentry',
    language: 'Idioma',
  },
  thumbs: {
    signedOut: 'Sesión cerrada',
    signIn: 'Iniciar sesión',
    welcome: 'Te damos la bienvenida',
    ready: 'Listo para grabar',
    recording: 'Grabando',
    paused: 'En pausa',
    writingNote: 'Escribiendo nota…',
    noteReady: 'Nota lista',
    completed: 'Completada',
    microphoneRequired: 'Se requiere micrófono',
    microphoneDisconnected: 'Micrófono desconectado',
    recordingTooShort: 'Grabación demasiado corta',
    notEnoughInfo: 'Información insuficiente',
    somethingWentWrong: 'Algo salió mal',
    summaryFailed: 'Error en el resumen',
    startRecording: 'Iniciar grabación',
    finish: 'Finalizar',
    pause: 'Pausar',
    recordMore: 'Seguir grabando',
    sendToEmr: 'Enviar al EMR',
    sendToEmrAgain: 'Enviar al EMR de nuevo',
    allowAccess: 'Permitir acceso',
    showDetails: 'Maximizar',
    microphone: 'Micrófono',
    settings: 'Configuración de la cita',
    seeNotesTab: 'Ver detalles en la pestaña Notas',
    canceled: 'Cita cancelada',
  },
  appointment: {
    noSummaryAvailableTitle: 'Aún no hay resumen disponible',
    noSummaryAvailableDescription:
      'Tu nota se generará aquí cuando finalice la sesión.',
    insightsTab: 'Insights',
    notesTab: 'Notas',
    transcriptTab: 'Transcripción',
    soapNotesTab: 'Notas SOAP',
    noMicrophoneSelected: 'No hay micrófono seleccionado',
    recordingTooShortTitle: 'Grabación demasiado corta',
    recordingTooShortBody:
      'Se necesitan al menos 10 segundos de audio para generar una nota. Sigue grabando y vuelve a intentarlo.',
    transcript: {
      empty: 'No hay transcripción.',
      viewTranscript: 'Ver transcripción',
      hideTranscript: 'Ocultar transcripción',
      sessionStartedAt: 'Sesión iniciada el {date} a las {time}',
      sessionResumedAt: 'Sesión reanudada el {date} a las {time}',
      sessionPausedAt: 'Sesión pausada el {date} a las {time}',
    },
    transition: {
      title: 'Tienes una sesión en curso para otra cita',
      description: 'Finaliza la sesión actual para continuar.',
      endSession: 'Finalizar sesión',
      sessionPausedToast: 'Tu sesión ha sido pausada',
      sessionPausedToastDescription:
        'Vuelve a la cita de {patientName} para reanudarla o generar una nota.',
    },
    presence: {
      title: '¿Sigues ahí?',
      description:
        'Esta sesión es más larga de lo habitual. Se pausará automáticamente en',
      pauseNow: 'Pausar ahora',
      continueRecording: 'Continuar grabación',
    },
    onboarding: {
      title: '¿Listo para grabar tu primera cita en Eluve?',
      subtitle: 'Cuéntanos un poco sobre ti para personalizar tu experiencia.',
      uiLanguageLabel: '¿En qué idioma debe estar Eluve?',
      uiLanguageTooltip:
        'Puedes cambiarlo en cualquier momento desde el menú de configuración en el encabezado.',
      uiLanguageAriaLabel: 'Idioma de la interfaz',
      uiLanguageSearchPlaceholder: 'Buscar idiomas...',
      specialtyLabel: '¿Cuál es tu especialidad?',
      specialtyTooltip:
        'Esto nos ayuda a adaptar las plantillas de notas y la terminología a tu área de práctica.',
      specialtyPlaceholder: 'Seleccionar especialidad...',
      inputLanguageLabel: '¿En qué idioma hablas con tus pacientes?',
      inputLanguageTooltip:
        'Esto mejora la precisión de la transcripción al optimizarla para tu idioma hablado.',
      inputLanguageAriaLabel: 'Idioma hablado',
      outputLanguageLabel: '¿En qué idioma escribes tus notas SOAP?',
      outputLanguageAriaLabel: 'Idioma de las notas',
      save: 'Guardar',
      saveFailed:
        'No se pudieron guardar las preferencias. Por favor, intenta de nuevo.',
    },
    audioFile: {
      unsupportedFormat: 'Formato no compatible: .{extension}. Usa {formats}.',
      tooLarge: 'Archivo demasiado grande ({size}MB). Máximo 100MB.',
      uploaded: 'Archivo de audio subido. El procesamiento comenzará en breve.',
      processFailed: 'No se pudo procesar el archivo de audio.',
      processing: 'Procesando...',
      dropHere: 'Suelta el archivo de audio para procesarlo',
    },
    session: {
      unavailableTitle: 'Sesión no disponible',
      unavailableDescription:
        'No se pudo cargar tu sesión. Haz clic en Reintentar para restaurarla.',
      retry: 'Reintentar',
    },
    languageSelector: {
      regenerateFailed:
        'No se pudo regenerar el resumen. Por favor, intenta de nuevo.',
      regeneratingToast: 'Regenerando tu resumen en el nuevo idioma',
      regeneratingToastDescription: 'Tu nuevo resumen estará disponible pronto',
      spokenLanguageWarning:
        'Cambiar el idioma hablado volverá a transcribir toda la conversación desde el inicio de la sesión.',
      notesLanguageWarning:
        'Cambiar el idioma de las notas generará una nota nueva',
      notesLanguageWarningDescription:
        'La nueva nota se generará en {language}.',
      notesLanguageWarningOverwrite: 'Esto sobrescribirá el resumen actual.',
      theSelectedLanguage: 'el idioma seleccionado',
      spokenLanguageLabel: 'Idioma hablado:',
      spokenLanguageAriaLabel: 'Idioma hablado',
      notesLanguageLabel: 'Idioma de las notas:',
      notesLanguageAriaLabel: 'Idioma de las notas',
    },
    signedSummary: {
      amendmentFailed: 'No se pudo crear la enmienda',
      additionalNotes: 'Notas adicionales',
    },
    summarization: {
      noSummaryAvailable: 'No hay resumen disponible para esta cita',
    },
    summaryFailed: {
      regenerateNote: 'Intentar de nuevo',
    },
    insights: {
      differentialDiagnoses: 'Diagnósticos diferenciales',
      generate: 'Generar',
      generateBlockedTooltip:
        'Los insights previos a la visita solo se generan para visitas próximas o en curso.',
      blockedTitle: 'Insights previos a la visita no disponibles',
      blockedMessage:
        'Esta cita ya no está próxima, por lo que no se generan nuevos insights previos a la visita.',
      title: 'Insights de IA',
      tooltip:
        'Insights generados por IA a partir del historial del paciente para apoyar tus decisiones clínicas.',
      collapse: 'Contraer',
      show: 'Mostrar',
      loading: {
        reviewingPriorNotes: 'Revisando notas previas del paciente…',
        summarizingDocumentation: 'Resumiendo documentación…',
        gatheringInsights: 'Recopilando insights…',
      },
      pvi: {
        noDataTitle: 'No hay notas previas para este paciente',
        noDataMessage:
          'Eluve no tiene datos previos de este paciente. Comienza a grabar tu cita para generar la primera nota de este paciente.',
        errorTitle: 'No se pudieron generar los insights de IA',
        errorMessage:
          'Eluve no pudo generar insights para este paciente. Aún puedes grabar y resumir tu cita.',
      },
    },
    ddx: {
      couldNotGenerate: 'No se pudo generar el DDx',
      couldNotGenerateDescription:
        'Eluve no pudo generar diagnósticos diferenciales para esta cita.',
      notGenerated:
        'Aún no hay diagnósticos disponibles. Vuelve a consultar después de grabar y resumir tu cita.',
      empty: 'No se identificaron diagnósticos diferenciales.',
      considerations: 'CONSIDERACIONES',
      feedbackPrompt: '¿Te resultaron útiles estos diagnósticos?',
      loading: {
        analyzing: 'Analizando el contexto clínico…',
        identifying: 'Identificando diagnósticos diferenciales…',
        evaluating: 'Evaluando la evidencia diagnóstica…',
      },
    },
    feedback: {
      yes: 'Sí',
      no: 'No',
      additionalFeedback: '¿Algún comentario adicional?',
      ratingPrompt:
        '¿Cómo calificarías tu experiencia con Eluve en esta sesión?',
      ratingPoor: 'Mala',
      ratingOkay: 'Regular',
      ratingExcellent: 'Excelente',
      tellMore: '¡Cuéntanos más!',
      submit: 'Enviar comentarios',
      wasNoteHelpful: '¿Te resultó útil esta nota?',
      summarySaveFailed: 'No se pudieron guardar los comentarios',
      summaryUpdateFailed: 'No se pudieron actualizar los comentarios',
      summaryTagsUpdateFailed:
        'No se pudieron actualizar las etiquetas de comentarios',
      thanksPositive:
        '¡Gracias por tus comentarios! ¡Nos alegra que te haya gustado!',
      thanksNegative:
        'Lamentamos que este resumen no cumpliera tus expectativas. Cada comentario que nos das (positivo o negativo) nos ayuda a mejorar nuestros resultados futuros.',
      wasHelpful: '¿Te resultó útil?',
      commentsOptional: 'COMENTARIOS (OPCIONAL)',
      shareFeedback: 'Compartir comentarios',
      tellMoreOptional: 'Cuéntanos más (opcional)',
      submitShort: 'Enviar',
      closeForm: 'Cerrar formulario de comentarios',
      thumbsUp: 'Me gusta',
      thumbsDown: 'No me gusta',
      tagTooLong: 'Demasiado larga',
      tagWrongInformation: 'Información incorrecta',
      tagTooSlow: 'Demasiado lenta',
      tagNotEnoughInformation: 'Información insuficiente',
      tagOtherIssue: 'Otro problema',
      tagMissingDetails: 'Faltan detalles importantes',
      tagIrrelevantDetails: 'Contiene detalles irrelevantes',
      tagUnspokenDetails: 'Contiene detalles no mencionados',
      tagIncorrectNumber: 'Número incorrecto',
      tagIncorrectBodyPart: 'Parte del cuerpo incorrecta',
      tagBadFormatting: 'Formato deficiente',
      tagTooVerbose: 'Demasiado extensa',
    },
    summary: {
      copiedToClipboard: 'Resumen copiado al portapapeles',
      copySummary: 'Copiar resumen',
    },
    syncEhr: {
      syncFailed: 'Error de sincronización',
      syncedSuccessfully: '¡Sincronizado con éxito!',
      notAvailableLocation:
        'La sincronización de notas no está disponible para tu ubicación.',
      notOwner: 'Solo el propietario de la cita puede sincronizar esta nota.',
      chartNotModified:
        'El expediente no ha sido modificado. Por favor, completa el expediente antes de sincronizar la nota.',
      selectPatient: 'Selecciona un paciente antes de sincronizar la nota.',
      selectLocation: 'Selecciona una ubicación antes de sincronizar la nota.',
      notAvailableNow:
        'La sincronización de notas no está disponible en este momento.',
      pushToEmr: 'Enviar al EMR',
      pushAgainNotAvailable:
        'Esta nota ya fue enviada a tu EHR y no puede enviarse de nuevo.',
      syncAgain: 'Sincronizar de nuevo',
    },
    settings: {
      spokenLanguageDescription:
        'Cambiar el idioma hablado volverá a transcribir toda la conversación desde el inicio de la sesión.',
      retranscribeSession: 'Volver a transcribir la sesión',
      notesLanguageDescription:
        'Esto regenerará la nota en el idioma seleccionado y sobrescribirá la actual.',
      regenerateNote: 'Regenerar nota',
      templateDescription:
        'Esto regenerará la nota con la plantilla seleccionada y sobrescribirá la actual.',
      filterPlaceholder: 'Escribe para filtrar...',
      noLanguagesFound: 'No se encontraron idiomas.',
      noTemplatesFound: 'No se encontraron plantillas.',
      templateUpdateFailed: 'No se pudo actualizar la plantilla.',
      spokenLanguageUpdateFailed: 'No se pudo actualizar el idioma hablado.',
      noteLanguageUpdateFailed: 'No se pudo actualizar el idioma de las notas.',
      spokenLanguageItem: 'Idioma hablado: {language}',
      noteLanguageItem: 'Idioma de las notas: {language}',
      templateItem: 'Plantilla: {template}',
      notSelected: 'Sin seleccionar',
      testSentry: 'Test Sentry',
      spokenLanguageHeading: 'Idioma hablado',
      noteLanguageHeading: 'Idioma de las notas',
      templateHeading: 'Plantilla',
      confirmTitle: '¿Estás seguro?',
    },
    summaryStates: {
      pendingDataTitle: 'Preparando la generación de la nota...',
      pendingDataDescription:
        'Todavía estamos subiendo y procesando algunos archivos. Cuando termine, generaremos tu nota.',
      inProgressTitle: 'Generando nota...',
      inProgressDescription:
        '¡Puedes salir de esta página y comenzar una nueva sesión! Te avisaremos cuando tu nota esté lista.',
      takingLongerTitle: 'Esto está tardando más de lo esperado...',
      takingLongerDescription:
        'Seguimos trabajando en tu nota. ¡Puedes salir de esta página y comenzar una nueva sesión! Te avisaremos cuando esté lista.',
      regenerateHint:
        'Puedes intentar regenerar la nota con una plantilla diferente.',
    },
    contentStates: {
      notFoundTitle: 'Cita no encontrada',
      notFoundDescription:
        'No pudimos encontrar esta cita en tu EMR. Intenta abrir otra cita.',
      canceledTitle: 'Cita cancelada',
      canceledDescription:
        'Esta cita fue cancelada en tu EMR. Intenta abrir otra cita.',
      notStartedTitle: 'Listo para grabar',
      notStartedDescription: 'Presiona "Iniciar grabación" para comenzar.',
      recordingTitle: 'Grabación en curso',
      recordingDescription:
        'Habla con naturalidad. Describe la historia clínica, los síntomas y tu evaluación clínica.',
      pausedTitle: 'Grabación pausada',
      pausedDescription:
        'Presiona "Seguir grabando" para continuar, o "Finalizar" para escribir la nota.',
      pendingAudioTitle: 'Subiendo grabación…',
      pendingAudioDescription: 'Esto solo tomará un momento.',
      summarizingTitle: 'Escribiendo nota…',
      summarizingDescription: 'Esto suele tardar menos de un minuto.',
      takingLongerTitle: 'Esto está tardando más de lo habitual…',
      takingLongerDescription:
        'Seguimos escribiendo tu nota. Puedes salir de esta página. Te avisaremos cuando esté lista.',
      stuckTitle: 'Tu grabación no se completó',
      stuckDescription: 'La subida fue interrumpida. Intenta de nuevo.',
      degradedTranscriptTitle: 'No se pudo procesar parte del audio',
      degradedTranscriptDescription:
        'Intenta de nuevo. Esto suele solucionarlo.',
      noteTooShortTitle: 'Grabación demasiado corta',
      noteTooShortDescription:
        'Solo captamos unas pocas palabras de tu grabación. Para escribir una nota precisa, presiona "Seguir grabando" y describe los detalles clínicos.',
      noteNoContextTitle: 'Detalle clínico insuficiente',
      noteNoContextDescription:
        'Para escribir una nota precisa, presiona "Seguir grabando" y describe los detalles clínicos. Como síntomas, diagnóstico o plan.',
      failedTitle: 'Algo salió mal',
      failedDescription:
        'Nuestros ingenieros lo saben y lo están investigando. Por favor, intenta de nuevo.',
      templateFailedTitle: 'Esta plantilla no funcionó',
      templateFailedDescription:
        'No pudimos escribir tu nota. Selecciona otra plantilla abajo y luego intenta de nuevo.',
      micPermissionTitle: 'Se necesita acceso al micrófono',
      micPermissionDescription:
        'Eluve necesita tu micrófono para grabar audio.',
      micDisconnectTitle: 'Micrófono desconectado',
      micDisconnectDescription:
        'Grabación pausada. Elige un micrófono y luego presiona "Seguir grabando".',
      recordingErrorTitle: 'Grabación interrumpida',
      recordingErrorDescription: 'Intenta de nuevo o actualiza la página.',
      sessionExpiredTitle: 'Sesión expirada',
      sessionExpiredDescription:
        'Tu grabación está guardada. Actualiza la página para continuar.',
      lowStorageTitle: 'Tu dispositivo tiene poco almacenamiento',
      lowStorageDescription:
        'Libera espacio en tu computadora para seguir grabando. Lo que grabaste está guardado.',
      recordingErrorRefresh: 'Actualizar',
      recordingErrorRetry: 'Intentar de nuevo',
      recordingErrorDismiss: 'Descartar',
    },
    summarizationError: {
      somethingWrong: 'Algo salió mal',
      failedToGenerate: 'No se pudo generar la nota',
      teamInvestigating:
        'Nuestro equipo está investigando el problema. Puedes intentar de nuevo o escribir a support@eluve.com si necesitas ayuda.',
      recordingTooShort:
        'Esta grabación es demasiado corta o no tiene suficiente información clínica para generar una nota. Reanuda la grabación para agregar más contenido.',
      templateSelectionRequired:
        'La generación de la nota falló. Selecciona otra plantilla abajo y haz clic en Regenerar para intentar de nuevo.',
    },
    summaryStuck: {
      description:
        'No pudimos generar tu nota. Puedes intentar de nuevo o escribir a support@eluve.com si necesitas ayuda.',
      retryButton: 'Intentar de nuevo',
      retrySuccessTitle: 'Intentando de nuevo',
      retrySuccessDescription: 'Tu nota estará disponible pronto',
      retryErrorTitle: 'Error al reintentar',
      retryErrorDescription: 'Por favor, intenta de nuevo o contacta a soporte',
    },
    regenerate: {
      button: 'Regenerar',
      title: 'Regenerando tu resumen',
      overwriteWarning: 'Esto sobrescribirá el resumen actual.',
      templateLabel: 'Tu nueva nota se generará con la siguiente plantilla:',
      successDescription: 'Tu nuevo resumen estará disponible pronto',
      errorTitle: 'No se pudo regenerar el resumen',
      errorDescription: 'Por favor, intenta de nuevo',
    },
    disabledTooltips: {
      noMicTitle: 'No hay micrófono seleccionado',
      noAudioTitle: 'No se detecta audio',
      noMicDescription: 'Por favor, selecciona uno para comenzar a grabar.',
      noAudioDescription:
        'Por favor, activa tu micrófono o selecciona otro para comenzar a grabar.',
      storageFullTitle: 'Almacenamiento insuficiente',
      storageFullDescription:
        'Tu dispositivo tiene poco almacenamiento, lo que puede impedir que una grabación se guarde. Libera espacio e intenta de nuevo.',
      recordingTooShortTitle: 'Grabación demasiado corta',
      recordingTooShortDescription:
        'Necesitamos al menos 10 segundos de audio para escribir una nota. Por favor, sigue grabando.',
    },
    completed: {
      synced: 'Sincronizado',
      syncedWithDate: 'Sincronizado: {date}',
    },
    active: {
      pausedWithDuration: 'Pausado - {duration}',
      pausedWithDurationDot: 'Pausado · {duration}',
      pauseRecording: 'Pausar grabación',
      resumeRecording: 'Reanudar grabación',
    },
  },
  summaryComparison: {
    triggerTitle: 'RESULTADO ALTERNATIVO',
    triggerDescription: '¡Ve las opciones alternativas y elige tu favorita!',
    thankYouTitle: 'GRACIAS',
    thankYouDescription: 'Tu resumen se actualizó según tu selección.',
    selectionFailed:
      'No se pudo registrar tu selección. Por favor, intenta de nuevo.',
    updateDefaultFailed:
      'No se pudo actualizar tu plantilla predeterminada. Por favor, intenta de nuevo.',
    updateDefaultTitle: '¿Actualizar tu plantilla predeterminada?',
    updateDefaultDescription:
      'La nota que seleccionaste se generó con una plantilla distinta a tu plantilla predeterminada actual. ¿Deseas establecerla como tu nueva plantilla predeterminada?',
    updateDefaultConfirm: 'Actualizar predeterminada',
    dialogTitle: '¿Qué nota prefieres?',
    dialogDescription:
      'Tu elección nos ayuda a mejorar y ofrecer mejores resúmenes en el futuro.',
    preferThisNote: 'Prefiero esta nota',
    generatedIn: 'Generado en {seconds} segundos',
  },
  blocks: {
    transcriptionWarning: {
      title: 'La transcripción en vivo ha sido pausada.',
      description:
        'Tu grabación se sigue guardando. La transcripción se completará después de la cita.',
    },
    language: {
      searchPlaceholder: 'Buscar idiomas...',
    },
    specialty: {
      defaultPlaceholder: 'Selecciona una especialidad',
      other: 'Otra',
      clearSpecialty: 'Borrar especialidad',
      enterSpecialty: 'Ingresa tu especialidad',
      searchPlaceholder: 'Buscar especialidades...',
    },
    callout: {
      dismissTip: 'Descartar consejo: {title}',
      dontShowAgain: 'No volver a mostrar',
    },
    showAllAppointments: 'Mostrar todas las citas',
    dateNavigator: {
      today: 'Hoy',
    },
    settingsImageUpload: {
      upload: 'Subir',
      uploadAria: 'Subir {label}',
      deleteAria: 'Eliminar {label}',
    },
  },
  chat: {
    header: {
      patientInsights: 'Insights del paciente',
      eluveInsights: 'Eluve Insights',
      newChat: 'Nuevo chat',
      pastChats: 'Chats anteriores',
      close: 'Cerrar',
    },
    threads: {
      titlePlaceholder: 'Título de la conversación',
      unnamed: 'Conversación sin nombre',
      conversationAria: 'Conversación: {title}',
      loadMore: 'Cargar más',
    },
    emptyState: {
      heading: 'Pregúntale lo que quieras a Eluve',
      more: 'Más',
      less: 'Menos',
      disclaimer:
        'La IA puede cometer errores. Por favor, verifica las respuestas.',
      seeTermsPrefix: 'Consulta los',
      termsLinkLabel: 'Términos y Condiciones',
      seeTermsSuffix: 'para más información.',
    },
    input: {
      placeholder: 'Pregunta lo que quieras',
      attachFile: 'Adjuntar archivo',
      stop: 'Detener',
      send: 'Enviar mensaje',
    },
    tools: {
      getPatientNotesActive: 'Buscando registros del paciente',
      getPatientNotesCompleted: 'Registros del paciente encontrados',
      createSpecTemplateActive: 'Creando vista previa de la plantilla',
      createSpecTemplateCompleted: 'Plantilla lista',
      saveSpecTemplateActive: 'Guardando plantilla',
      saveSpecTemplateCompleted: 'Plantilla guardada',
      draftDocumentActive: 'Redactando documento',
      draftDocumentCompleted: 'Borrador del documento listo',
      generateDocumentActive: 'Guardando documento',
      generateDocumentCompleted: 'Documento guardado',
      getAppointmentNoteActive: 'Leyendo la nota de la cita',
      getAppointmentNoteCompleted: 'Nota de la cita cargada',
      updateAppointmentNoteActive: 'Actualizando la nota de la cita',
      updateAppointmentNoteCompleted: 'Nota de la cita actualizada',
      getCustomInstructionsActive: 'Leyendo instrucciones personalizadas',
      getCustomInstructionsCompleted: 'Instrucciones personalizadas cargadas',
      suggestCustomInstructionActive: 'Preparando sugerencia',
      suggestCustomInstructionCompleted: 'Sugerencia lista',
      saveCustomInstructionActive: 'Guardando instrucción personalizada',
      saveCustomInstructionCompleted: 'Instrucción personalizada guardada',
      queryFhirResourcesActive: 'Buscando registros clínicos',
      queryFhirResourcesCompleted: 'Registros clínicos encontrados',
      patientNotesAgentActive: 'Consultando al Agente de Notas del Paciente',
      patientNotesAgentCompleted: 'El Agente de Notas del Paciente respondió',
      conversationStarterAgentActive:
        'Consultando al Agente de Inicios de Conversación',
      conversationStarterAgentCompleted:
        'El Agente de Inicios de Conversación respondió',
      defaultActive: 'Trabajando',
      defaultCompleted: 'Listo',
      appointmentNote: 'Nota de la cita',
    },
    document: {
      drafting: 'Redactando documento…',
      saveFailed:
        'No se pudo guardar el documento. Por favor, intenta de nuevo.',
      titlePlaceholder: 'Título del documento',
      add: 'Agregar documento',
      copy: 'Copiar',
      copied: 'Copiado',
      discard: 'Descartar',
      addedToNotes: 'Agregado a las notas',
      discarded: 'Descartado',
      discardDialogTitle: '¿Descartar documento?',
      discardDialogDescription:
        'Esto descartará el borrador del documento. Siempre puedes pedirle al chat que genere uno nuevo.',
      discardMessage: 'Descarta el documento, no lo necesito.',
    },
    starters: {
      preparingSuggestion: 'Preparando sugerencia…',
      suggestedTitle: 'Inicio de conversación sugerido',
      outputPreference: 'Preferencia de salida (opcional)',
      none: 'Ninguna',
      dismiss: 'Descartar',
      dismissMessage: 'No, gracias, no lo guardes como inicio de conversación.',
      confirmMessage: 'Sí, guárdalo como inicio de conversación.',
      confirmWithPreferenceMessage:
        'Sí, guárdalo como inicio de conversación con la preferencia de salida "{label}".',
      save: 'Guardar inicio',
      saving: 'Guardando inicio de conversación…',
      saved: 'Inicio de conversación guardado',
      starterAria: 'Inicio de conversación: {title}',
    },
    instructions: {
      preparingSuggestion: 'Preparando sugerencia…',
      suggestedTitle: 'Instrucción personalizada sugerida',
      dismiss: 'Descartar',
      dismissMessage:
        'No, gracias, no lo guardes como instrucción personalizada.',
      confirmMessage: 'Sí, guárdalo como instrucción personalizada.',
      save: 'Guardar instrucción',
    },
    feedback: {
      wasHelpful: '¿Te resultó útil esta respuesta?',
      sendFailed: 'No se pudieron enviar los comentarios',
      updateFailed: 'No se pudieron actualizar los comentarios',
      commentFailed: 'No se pudo guardar el comentario',
      thanksPositive:
        '¡Gracias por tus comentarios! ¡Nos alegra que esta respuesta te haya sido útil!',
      thanksNegative:
        'Lamentamos que esta respuesta no cumpliera tus expectativas. Cada comentario nos ayuda a mejorar los resultados futuros.',
    },
    citations: {
      sources: 'Fuentes:',
      viewLess: 'Ver menos',
      viewAll: 'Ver todas ({count})',
    },
    pdf: {
      passwordProtectedTooltip:
        'Este PDF está protegido con contraseña. ¡Haz clic para desbloquearlo!',
      previewTooltip: 'Previsualizar este PDF',
      removeFile: 'Quitar {fileName}',
      passwordRequiredTitle: 'Contraseña requerida',
      passwordRequired: 'La contraseña es obligatoria',
      enterPasswordFor:
        'Por favor, ingresa la contraseña para desbloquear {fileName}.',
      fileFallback: 'el archivo',
      passwordPlaceholder: 'Ingresa la contraseña',
      invalidPassword: 'Contraseña incorrecta',
      unlockedSuccess: 'PDF desbloqueado con éxito',
      unlocking: 'Desbloqueando...',
      unlock: 'Desbloquear',
    },
    knowledgeSources: {
      updateFailed: 'No se pudieron actualizar las fuentes de conocimiento',
      saveFileFailed: 'No se pudo guardar el archivo subido',
      removeFileFailed: 'No se pudo quitar el archivo',
      downloadFailed: 'No se pudo descargar el archivo',
      uploadedFiles: 'Archivos subidos',
      uploadFiles: 'Subir archivos',
      noFiles: 'Aún no hay archivos subidos',
    },
    recentNotes: {
      label: 'Notas recientes',
      tooltip:
        'Es posible que el chat no tenga acceso a todas las notas. Lee las notas recientes de este paciente desde tu EMR, así como las notas creadas en Eluve.',
    },
  },
  errors: {
    homeScreenNotFound: {
      title: 'Pantalla de inicio no disponible',
      message:
        'Lo sentimos, no pudimos cargar la pantalla de inicio del EHR. Por favor, intenta actualizar la página.',
    },
    notLoggedIn: {
      title: 'No autorizado',
      message:
        'Por favor, inicia sesión en tu cuenta del EHR en una nueva pestaña e intenta de nuevo.',
    },
    soapTemplateNotFound: {
      title: 'Falta la plantilla',
      message:
        'Plantilla no encontrada: "SOAP Template". Por favor, verifica que la plantilla exista e intenta de nuevo.',
    },
    noteEditorNotFound: {
      title: 'Editor de notas no disponible',
      message:
        'No pudimos encontrar el editor de notas en el EHR. Por favor, asegúrate de que esté presente e intenta de nuevo.',
    },
    templateNotSupported: {
      title: 'Plantilla no compatible',
      message:
        'La plantilla seleccionada no es compatible con el EHR. Por favor, selecciona otra plantilla e intenta de nuevo.',
    },
    failedToSign: {
      title: 'Error al firmar la entrada del expediente',
      message:
        'Ocurrió un problema al firmar la entrada del expediente. Por favor, intenta de nuevo.',
    },
    genericError: {
      title: 'Algo salió mal',
      message:
        'Nuestro equipo está investigando el problema. Puedes intentar de nuevo o escribir a support@eluve.com si necesitas ayuda.',
    },
    timeout: {
      title: 'La solicitud tardó demasiado',
      message:
        'Tu solicitud tardó demasiado en procesarse. Por favor, verifica tu conexión a internet e intenta de nuevo.',
    },
    failedToImport: {
      title: 'Error al importar',
      message:
        'No se pudieron importar los datos. Por favor, verifica los datos e intenta de nuevo.',
    },
    staffMismatch: {
      title: 'El propietario de la cita no coincide',
      message:
        'El propietario de la cita no coincide con el usuario actual en el EHR. Por favor, asegúrate de haber iniciado sesión con el usuario correcto e intenta de nuevo.',
    },
    invalidStatePageRefreshRequired: {
      title: 'Sesión expirada',
      message:
        'Hemos detectado un estado inconsistente. Por favor, actualiza la página para continuar.',
    },
    micDisconnected: {
      title: 'Micrófono no detectado',
      message:
        'Tu grabación ha sido pausada. Por favor, actualiza la selección de micrófono para reanudar.',
    },
    micPermissionsDenied: {
      title: 'Se requiere acceso al micrófono',
      message:
        'Eluve necesita permiso para acceder a tu micrófono y grabar audio.',
    },
    domInjectionFailed: {
      title: 'Eso no funcionó',
      message: 'Intenta copiar la nota sección por sección.',
    },
    patientDeleted: {
      title: 'Paciente no encontrado en el EHR',
      message:
        'El paciente asociado a esta cita fue eliminado en tu EHR. Por favor, asegúrate de que el paciente exista o selecciona el paciente correcto en Eluve e intenta de nuevo.',
    },
    appointmentNotFound: {
      title: 'Cita no encontrada en el EHR',
      message:
        'Es posible que la cita haya sido eliminada o reprogramada en tu EHR. Por favor, verifica que la cita exista e intenta de nuevo.',
    },
    userIdMismatch: {
      title: 'Usuario no coincide',
      message:
        'El usuario con sesión iniciada no coincide con el usuario actual del EHR. Reautenticando automáticamente.',
    },
    extensionConnectionLost: {
      title: 'Conexión con la extensión perdida',
      message:
        'Se perdió la conexión con la extensión de Eluve. Por favor, actualiza la página e intenta de nuevo.',
    },
    insufficientStorage: {
      title: 'Grabación pausada por poco almacenamiento',
      message:
        'Tu dispositivo se quedó con poco almacenamiento, así que pausamos la grabación para conservar lo capturado. Libera espacio para reanudar, o finaliza para generar tu nota.',
    },
    ehrIntegrationNotAvailable: {
      title: 'Integración con el EHR no disponible',
      message:
        'Este proveedor de EHR requiere la integración con la extensión del navegador, que no está disponible en este contexto.',
    },
    appointmentAlreadySigned: {
      title: 'Nota ya firmada',
      message:
        'Esta nota ya está firmada, por lo que la cita ya no puede grabarse ni regenerarse.',
    },
  },
  sidebar: {
    overview: 'Vista general',
    appointments: 'Citas',
    patients: 'Pacientes',
    services: 'Servicios',
    careLibrary: 'Biblioteca de cuidados',
    tasks: 'Tareas',
    settings: 'Configuración',
    support: 'Soporte',
  },
  userMenu: {
    impersonatingUser: 'Suplantando usuario',
    exitImpersonation: 'Salir de la suplantación',
    switchOrganization: 'Cambiar de organización',
    unnamedOrganization: 'Organización sin nombre',
    organizationSettings: 'Configuración de la organización',
    betaFeatures: 'Funciones beta',
    systemStatus: 'Estado del sistema',
    contactUs: 'Contáctanos',
    logOut: 'Cerrar sesión',
    endRecordingToSwitchTitle: '¿Finalizar la grabación para cambiar?',
    endRecordingToSwitchDescription:
      'Tienes una grabación en curso. Cambiar de organización finalizará la sesión de grabación actual.',
    keepRecording: 'Seguir grabando',
    ending: 'Finalizando…',
    endAndSwitch: 'Finalizar y cambiar',
  },
  settings: {
    nav: {
      backToApp: 'Volver a la aplicación',
      account: 'Cuenta',
      preferences: 'Preferencias',
      organization: 'Organización',
      userSettings: 'Configuración de usuario',
      audioDisplay: 'Audio y pantalla',
      shortcuts: 'Atajos',
      appGrants: 'Permisos de aplicaciones',
      note: 'Nota',
      tasks: 'Tareas',
      templates: 'Plantillas',
      chat: 'Chat',
      insights: 'Insights',
      general: 'General',
      members: 'Miembros',
      sessionTypes: 'Tipos de sesión',
      locations: 'Ubicaciones',
      services: 'Servicios',
      providers: 'Profesionales',
      userGroups: 'Grupos de usuarios',
      noteRecovery: 'Recuperación de notas',
      carePlans: 'Planes de cuidado',
    },
    common: {
      save: 'Guardar',
      add: 'Agregar',
      delete: 'Eliminar',
      reset: 'Restablecer',
      examples: 'EJEMPLOS',
      resetToDefaultTitle: '¿Restablecer la configuración predeterminada?',
    },
    user: {
      generalDescription: 'Tu información básica de perfil y cuenta.',
      email: 'Correo electrónico',
      fullName: 'Nombre completo',
      specialty: 'Especialidad',
      signature: 'Firma',
      providerDetailsTitle: 'Datos del proveedor médico',
      providerDetailsDescription:
        'Ingresa tu número de licencia, número NPI y cualquier otro dato relevante sobre tu práctica médica.',
      baaTitle: 'Acuerdo de Socios Comerciales (BAA)',
      baaSafeguarding:
        'Nos importa proteger tus datos. Tu BAA es parte de mantener el cumplimiento de HIPAA.',
      baaRequestPrefix:
        'Si tu organización requiere una versión firmada por separado del BAA para fines de archivo, puedes solicitarla escribiéndonos a',
      viewBaa: 'Ver BAA',
      downloadPdf: 'Descargar PDF',
    },
    audio: {
      microphone: 'Micrófono',
      microphoneDescription:
        'Prueba y elige la entrada de audio para las grabaciones.',
      notifications: 'Notificaciones',
      soundEffect: 'Efecto de sonido',
      soundEffectDescription:
        'Reproduce un tono al iniciar o detener una grabación.',
      previewSound: 'Escuchar sonido',
      play: 'Reproducir',
      language: 'Idioma',
      interfaceLanguageLabel: 'Idioma de la interfaz',
      interfaceLanguageDescription:
        'Tu idioma preferido para la interfaz de Eluve.',
      interfaceLanguageSearchPlaceholder: 'Buscar idiomas...',
      privacyDisplay: 'Privacidad y pantalla',
      privacyMode: 'Modo de privacidad',
      privacyModeDescription:
        'Oculta la información del paciente en tu pantalla.',
      privacyModeToggleHintPrefix:
        'Actívalo o desactívalo desde cualquier lugar con',
      examplePatientName: 'Ejemplo de nombre de paciente:',
      showAppointmentOwners: 'Mostrar responsables de las citas',
      showAppointmentOwnersDescription:
        'Muestra quién es responsable de cada cita en tu agenda.',
      shareScreenWithSupport: 'Compartir pantalla con soporte',
      shareScreenDescription:
        'Supervisa temporalmente las interacciones para depuración. Esto grabará tus interacciones en los productos de Eluve durante un máximo de {hours} horas y luego se desactivará automáticamente. Estas grabaciones se guardarán hasta por 30 días en un entorno que cumple con HIPAA y luego se eliminarán automáticamente.',
      micPreferencesChanged: 'Preferencias de micrófono actualizadas',
      micNotFound: 'No se pudo encontrar el micrófono',
      stopTest: 'Detener',
      test: 'Probar',
    },
    note: {
      title: 'Preferencias de notas',
      defaults: 'Valores predeterminados',
      defaultsDescription: 'Preferencias comunes aplicadas a cada nota nueva.',
      defaultTemplate: 'Plantilla predeterminada',
      defaultTemplateDescription:
        'El formato de nota aplicado a las sesiones nuevas.',
      customInstructions: 'Instrucciones personalizadas',
      customInstructionsDescription:
        'Personaliza tus notas proporcionando pautas específicas. Intenta ser lo más claro y conciso posible para obtener mejores resultados.',
      instructionsPlaceholder: 'Escribe tus instrucciones aquí',
      saveInstructionsFailed: 'No se pudieron guardar las instrucciones',
      exampleRemoveLifestyle:
        'Elimina las partes sobre consejería de estilo de vida',
      exampleIncludeQuotes: 'Incluye citas textuales del paciente',
      examplePlanBullets: 'Mantén la sección del plan en viñetas',
      exampleFollowUpTimeline:
        'Termina siempre con un cronograma de seguimiento',
    },
    tasks: {
      title: 'Preferencias de tareas',
      customInstructions: 'Instrucciones de extracción de tareas',
      customInstructionsDescription:
        'Describe qué tareas de seguimiento importan para tu flujo de trabajo: qué incluir, omitir o destacar. Se aplica cada vez que se extraen tareas de tus sesiones.',
      instructionsPlaceholder: 'Escribe tus instrucciones aquí',
      saveInstructionsFailed: 'No se pudieron guardar las instrucciones',
    },
    appGrants: {
      authorizedAgents: 'Agentes autorizados',
      authorizedAgentsDescription:
        'Agentes que has autorizado para actuar en tu nombre. Revocar obliga al agente a pedir tu consentimiento de nuevo antes de poder ejecutarse.',
    },
    organization: {
      layers: 'Capas',
      layersDescription:
        'Organiza Eluve para que se adapte mejor a tu consultorio.',
    },
    chat: {
      title: 'Preferencias de chat',
      resetSuccess: 'Configuración restablecida a los valores predeterminados',
      resetFailed: 'No se pudo restablecer la configuración',
      outputPreferences: 'Preferencias de salida',
      outputPreferencesSavedOne:
        '{count} preferencia guardada para el formato de respuesta y el nivel de detalle.',
      outputPreferencesSavedMany:
        '{count} preferencias guardadas para el formato de respuesta y el nivel de detalle.',
      outputPreferencesConfigure:
        'Configura el formato de respuesta, el nivel de detalle y las instrucciones personalizadas.',
      customInstructions: 'Instrucciones personalizadas',
      customInstructionsDescription:
        'Indicaciones adicionales que cada respuesta del chat debe seguir.',
      conversationStarters: 'Inicios de conversación',
      conversationStartersDescription:
        'Sugerencias que se muestran al iniciar un nuevo chat.',
      knowledgeBase: 'Base de conocimiento',
      knowledgeBaseDescription:
        'Selecciona qué fuentes de conocimiento incluir en el contexto del chat.',
      resetToDefault: 'Restablecer valores predeterminados',
      resetDialogDescription:
        'Esto eliminará toda tu configuración personalizada del chat, incluidas las instrucciones personalizadas, los inicios de conversación y los archivos subidos. El chat volverá a la configuración predeterminada. Esta acción no se puede deshacer.',
      customInstructionsPageTitle: 'Instrucciones personalizadas del chat',
      saveInstructionsFailed:
        'No se pudieron guardar las instrucciones personalizadas',
      exampleRemoveParts: 'Elimina las partes sobre...',
      exampleIncludeDiscussion: 'Incluye la discusión sobre...',
      exampleMoreConcise: 'Haz las notas más concisas',
      exampleMoreDetailsPlan: 'Agrega más detalles en la sección del plan',
      outputPreferencesPageDescription:
        'Configura el formato de respuesta, el nivel de detalle y las instrucciones personalizadas. Vincúlalas a los inicios de conversación.',
      preferenceCountOne: '{count} preferencia',
      preferenceCountMany: '{count} preferencias',
      editPreferenceLabel: 'Editar {label}',
      deletePreferenceLabel: 'Eliminar {label}',
      noOutputPreferences: 'No hay preferencias de salida configuradas',
      newOutputPreference: 'Nueva preferencia de salida',
      editOutputPreference: 'Editar preferencia de salida',
      deletePreferenceTitle: '¿Eliminar preferencia?',
      deletePreferenceDescription:
        'Esto eliminará esta preferencia de salida. Los inicios de conversación vinculados a ella se desvincularán. Esta acción no se puede deshacer.',
      savePreferenceFailed: 'No se pudo guardar la preferencia',
      deletePreferenceFailed: 'No se pudo eliminar la preferencia',
      updateInstructionsFailed:
        'No se pudieron actualizar las instrucciones personalizadas',
      updateContextSourcesFailed:
        'No se pudieron actualizar las fuentes de contexto',
      updateStartersFailed:
        'No se pudieron actualizar los inicios de conversación',
      updateFilesFailed: 'No se pudieron actualizar los archivos',
    },
    insights: {
      title: 'Preferencias de insights',
      savePreferences: 'Guardar preferencias',
      savePreferencesFailed: 'No se pudieron guardar las preferencias',
      preferencesSaved: 'Preferencias guardadas',
      previewAgainst: 'Previsualizar con',
      samplePatient: 'Paciente de ejemplo (Edward Hyde)',
      generatePreview: 'Generar vista previa',
      generatingPreview: 'Generando vista previa…',
      previewOutOfDate:
        'La vista previa está desactualizada. Haz clic en Generar para aplicar tus cambios.',
      noRegenNeeded: 'Ninguna cita próxima necesitaba regeneración',
      regeneratingOne: 'Regenerando insights para {count} cita',
      regeneratingMany: 'Regenerando insights para {count} citas',
      regenerateFailed: 'No se pudieron regenerar los insights',
      resetFailed: 'No se pudieron restablecer las preferencias',
      unsavedChangesTitle: 'Cambios sin guardar',
      unsavedChangesDescription:
        'Tienes cambios sin guardar. ¿Seguro que quieres salir de esta página? Tus cambios se perderán.',
      stay: 'Quedarme',
      leave: 'Salir',
      regenerateDialogTitle: '¿Regenerar PVI?',
      regenerateDialogDescriptionOne:
        'Tus cambios se aplicarán a las citas nuevas a partir de mañana. ¿Quieres regenerar los Insights de IA para tu {count} cita próxima?',
      regenerateDialogDescriptionMany:
        'Tus cambios se aplicarán a las citas nuevas a partir de mañana. ¿Quieres regenerar los Insights de IA para tus {count} citas próximas?',
      notNow: 'Ahora no',
      regenerateNow: 'Regenerar ahora',
      resetDialogDescription:
        'Esto eliminará tus preferencias personalizadas de insights y volverá a la configuración predeterminada. Esta acción no se puede deshacer.',
      previewInvalidResponse:
        'La vista previa devolvió una respuesta no válida. Por favor, intenta de nuevo.',
      previewGenerationFailed:
        'La generación de la vista previa falló. Por favor, intenta de nuevo.',
      previewTimedOut:
        'La generación de la vista previa tardó demasiado. Por favor, intenta de nuevo.',
      previewStartFailed:
        'No se pudo iniciar la generación de la vista previa. Por favor, intenta de nuevo.',
    },
  },
  appointmentPage: {
    gotIt: 'Entendido',
    save: 'Guardar',
    retry: 'Reintentar',
    tryAgain: 'Intentar de nuevo',
    areYouSure: '¿Estás seguro?',
    noLongerEditable: 'Ya no podrás editar esta nota en Eluve.',
    signNote: 'Firmar nota',
    previewPdf: 'Vista previa del PDF',
    pdfPrepareFailed: 'No se pudo preparar el PDF',
    experiments: 'Experimentos',
    canceledInEhr: 'Esta cita fue cancelada en tu EHR.',
    summaryReadyTitle: '¡Tu resumen está listo!',
    summaryReadyDescription: 'Tu resumen está listo para ser revisado.',
    summariesReadyTitle: '¡Tus resúmenes están listos!',
    summariesReadyDescription: 'Tus resúmenes están listos para ser revisados.',
    appointmentsHeading: 'Citas',
    appointmentsLoadFailed: 'No se pudieron cargar los datos de las citas.',
    saveBlockFailed: 'No se pudo guardar el bloque',
    improveYourNotes: 'Mejora tus notas',
    saveSummaryFailed: 'No se pudo guardar el resumen',
    disclaimer: 'Aviso legal',
    details: {
      title: 'Detalles',
      appointmentHeading: 'Cita',
      date: 'Fecha',
      time: 'Hora',
      location: 'Ubicación',
      type: 'Tipo',
      owner: 'Responsable',
      artifacts: 'Documentos generados',
      updateFailed: 'No se pudieron actualizar los detalles de la cita',
      noLocationSpecified: 'Sin ubicación especificada',
      noDateSpecified: 'Sin fecha especificada',
      startTime: 'Hora de inicio',
      end: 'Fin',
      noType: 'Sin tipo',
    },
    header: {
      selectPatientFirst: '¡Primero selecciona un paciente!',
      appointmentDetailsAria: 'Detalles de la cita',
    },
    emailSuperbill: {
      emailSubject: 'Tu Superbill del {date} - {tenantName}',
      emailBodyWithDoctor:
        'Hola{name}, adjunto encontrarás tu Superbill de tu visita con el Dr. {doctorName} el {date}.',
      emailBody:
        'Hola{name}, adjunto encontrarás tu Superbill de tu visita el {date}.',
      updateEmailFailed: 'No se pudo actualizar el correo del paciente.',
      sendFailed: 'No se pudo enviar el correo. ¡Inténtalo de nuevo más tarde!',
      sentTitle: 'Correo enviado',
      sentDescription: 'Tu correo se envió correctamente.',
      emailPdfTooltip: 'Enviar PDF por correo',
      title: 'Enviar correo al paciente',
      description: 'Revisa la información antes de enviar',
      emailAddress: 'Correo electrónico',
      updatePatientEmailLabel:
        'También actualizar el correo del paciente en su cuenta',
      subjectLabel: 'Asunto',
      subjectPlaceholder: 'Tu superbill',
      messageLabel: 'Mensaje',
      messagePlaceholder: 'Descripción',
      sendEmail: 'Enviar correo',
    },
    syncToEhr: {
      noteSignedSuccessfully: 'Nota firmada correctamente',
      noteDraftedSuccessfully: 'Borrador de nota creado correctamente',
      noteSynced: 'Tu nota se ha sincronizado.',
      signFailed: 'No se pudo firmar la nota.',
      syncedToEhr: 'Tu nota se sincronizó correctamente con {ehrName}.',
      syncedButSignFailed:
        'Tu nota se sincronizó correctamente con {ehrName}, pero ocurrió un error al firmar la nota en Eluve.',
      syncNotAvailable: 'La sincronización con el EHR no está disponible',
      syncToEhrButton: 'Sincronizar con EHR',
      signExternalChart: 'Firmar el expediente externo',
      exportedTo: 'El contenido de esta nota se exportará a {ehrName}',
      asA: 'como',
      signedNote: 'nota firmada',
      draftNote: 'borrador',
      forPatient: 'para el paciente',
      confirmSyncAndSign: 'Sí, sincronizar y firmar la nota',
      confirmSyncAsDraft: 'Sí, sincronizar la nota como borrador',
      syncingTo: 'Sincronizando con {ehrName}',
      syncingDescription:
        'Estamos sincronizando con {ehrName}. Esto solo tomará unos momentos.',
      addChartUrlTitle: 'Agregar URL del expediente',
      chartUrlPlaceholder: 'URL del expediente',
      addChartUrlAndSign: 'Agregar URL del expediente y firmar',
      manuallyAddChartUrl: 'Agregar URL del expediente manualmente',
    },
    signNoteOnEluve: {
      noteSigned: 'Tu nota ha sido firmada.',
      notOwner: 'Solo el responsable de la cita puede firmar esta nota.',
      chartNotModified:
        'El expediente no ha sido modificado. Completa el expediente antes de firmar la nota.',
      selectLocation: 'Selecciona una ubicación antes de firmar la nota.',
      selectPatient: 'Selecciona un paciente antes de firmar la nota.',
      confirmSignNote: 'Sí, firmar la nota',
    },
    signedSummary: {
      cancelAmendmentFailed: 'No se pudo cancelar la enmienda',
      submitAmendmentFailed: 'No se pudo enviar la enmienda',
      discardAmendment: 'Descartar enmienda',
      amendChart: 'Enmendar expediente',
    },
    audioLost: {
      title: 'Es posible que a esta cita le falte audio',
      lastAudioReceived:
        'El último audio que recibimos fue {ago}; parece que el dispositivo entró en suspensión o el navegador se suspendió después de eso. Lo que se habló durante ese tiempo no se capturó y no se puede recuperar.',
      noAudioSinceStart:
        'Comenzamos a grabar {ago}, pero no hemos recibido audio desde entonces; esto suele ocurrir cuando el dispositivo entra en suspensión o el navegador se suspende. Si estabas hablando, ese audio no se capturó y no se puede recuperar.',
      avoidNextTime: 'Para evitarlo la próxima vez:',
      onComputerLabel: 'En una computadora:',
      onComputerBody:
        'mantén esta pestaña del navegador abierta y en primer plano mientras grabas; no cambies a otra pestaña ni dejes que la computadora entre en suspensión.',
      onPhoneLabel: 'En un teléfono o tablet:',
      onPhoneBody:
        'mantén el navegador abierto y desactiva el bloqueo automático de pantalla para que la pantalla no se apague mientras grabas.',
      moreThanADayAgo: 'hace más de un día',
      aboutSecondsAgoOne: 'hace aproximadamente 1 segundo',
      aboutSecondsAgo: 'hace aproximadamente {count} segundos',
      aboutMinutesAgoOne: 'hace aproximadamente 1 minuto',
      aboutMinutesAgo: 'hace aproximadamente {count} minutos',
      aboutHoursAgoOne: 'hace aproximadamente 1 hora',
      aboutHoursAgo: 'hace aproximadamente {count} horas',
      agoAtTime: '{ago} (a las {time})',
    },
    presence: {
      title: 'Esta sesión es más larga de lo habitual',
      beforeCountdown:
        'Si quieres seguir grabando, haz clic en el botón de abajo. De lo contrario, en',
      afterCountdown:
        'la sesión se pausará automáticamente. Podrás reanudarla más tarde si lo necesitas.',
    },
    micFailed: {
      title: 'Tu micrófono se desconectó',
      description:
        'La grabación se pausó. Antes de reanudar, asegúrate de que el micrófono seleccionado abajo sea el que quieres usar.',
      stayPaused: 'Mantener en pausa',
    },
    recordingFailed: {
      title: 'La sesión ya no está en curso.',
      description: 'Actualiza la página para reanudar la sesión.',
      refreshPage: 'Actualizar página',
    },
    storageFailure: {
      quotaTitle: 'El almacenamiento de tu navegador está lleno',
      quotaDescription:
        'La grabación se pausó, pero el audio grabado hasta el momento está guardado. Libera espacio en el disco o actualiza la página para continuar. También puedes generar una nota ahora.',
      unhealthyTitle: 'El almacenamiento de tu navegador no está disponible',
      unhealthyDescription:
        'La grabación se pausó, pero el audio grabado hasta el momento está guardado. Actualiza la página para continuar, o prueba con otro navegador si sigue ocurriendo. También puedes generar una nota ahora.',
      continue: 'Continuar',
      refreshPage: 'Actualizar página',
      resumeBlockedTooltip:
        'El almacenamiento de tu navegador está lleno o no disponible. Actualiza la página para reanudar.',
    },
    newAppointment: {
      sessionSettings: 'Configuración de la sesión',
      noteSettings: 'Configuración de la nota',
      viewMoreOptions: 'Ver más opciones',
      viewLessOptions: 'Ver menos opciones',
      playDisclaimer: 'Reproducir el aviso legal antes de grabar',
      startSession: 'Iniciar sesión de grabación',
      selectMicrophoneTooltip:
        'Selecciona un micrófono para iniciar la sesión.',
      ownerOnlyStartTooltip:
        'Solo el responsable de la cita puede iniciar la sesión.',
      micAccessTooltip:
        'No se pudo acceder al micrófono. Revisa tu configuración',
      noAudioTooltip: 'No se detectó audio. Revisa tu micrófono y el volumen.',
      alreadyInSessionTooltip: 'Ya hay una cita en sesión.',
      sessionStartingTooltip: 'La sesión está iniciando, espera un momento...',
      manualChartingStartFailed:
        'No se pudo iniciar la sesión de documentación manual.',
      duplicatePreviousChart: 'Duplicar expediente anterior',
      selectChart: 'Seleccionar expediente',
      duplicateChartTitle: 'Duplicar expediente',
      duplicateChartDescription:
        'Selecciona el expediente que quieres duplicar',
      transcribeTab: 'Transcribir',
      manualChartingTab: 'Documentación manual',
    },
    chiefComplaint: {
      updateFailed: 'No se pudieron actualizar los detalles de la cita.',
      updateContext: 'Actualizar contexto',
      addContext: 'Agregar contexto',
      title: 'Motivo principal y razón de la visita',
      chiefComplaintLabel: 'Motivo principal',
      chiefComplaintPlaceholder: 'Escribe el motivo principal',
      reasonForVisitLabel: 'Razón de la visita',
      reasonForVisitPlaceholder: 'Escribe la razón de la visita',
    },
    summaryStates: {
      pendingDataDescription: 'Generaremos tu nota en breve.',
      takingLongerDescription:
        'Seguimos trabajando en tus notas. ¡Puedes salir de esta página y comenzar una nueva sesión! Te avisaremos cuando tu nota esté lista.',
      browserOnDevice: '{browser} en {device}',
      uploadNeededPrefixOne: 'Tu grabación todavía debe subirse desde',
      uploadNeededPrefixMany: 'Tus grabaciones todavía deben subirse desde',
      uploadNeededSuffixOne:
        '. Para generar tu nota, mantén Eluve abierto en ese navegador hasta que se complete.',
      uploadNeededSuffixMany:
        '. Para generar tu nota, mantén Eluve abierto en esos navegadores hasta que se completen.',
    },
    summaryFailed: {
      resumeRecording: 'Reanudar grabación',
    },
    previsitInsights: {
      title: 'Insights previos a la visita',
      tooltip:
        'Resumen generado por IA de las notas de visitas anteriores del paciente.',
      autoGenerateNotice:
        'Los insights previos a la visita se generan automáticamente unos {minutes} minutos antes de la visita.',
      generateNow: 'Generar ahora',
      selectPatient:
        'Selecciona un paciente para generar insights previos a la visita.',
    },
    documents: {
      deleted: 'Documento eliminado',
      deleteFailed: 'No se pudo eliminar el documento',
      renameFailed: 'No se pudo renombrar el documento',
      newDocument: 'Documento nuevo',
      generated: 'Documento generado',
      unknownError: 'Ocurrió un error desconocido.',
      selectPatientFirst:
        'Primero selecciona un paciente para crear documentos',
    },
    carePlan: {
      preparing: 'Preparando tu plan de cuidado…',
      willBeGenerated:
        'El plan de cuidado se generará a partir de los hallazgos aplicados.',
    },
    filesDrawer: {
      trigger: 'Archivos',
      title: 'Archivos de la cita',
      filesWillBeRemoved:
        'Los archivos se eliminarán de tu dispositivo cuando la cita se complete',
      noLocalFiles: 'No hay archivos locales en este dispositivo.',
      download: 'Descargar',
    },
    additionalNotes: {
      updateFailed: 'No se pudieron actualizar las notas adicionales',
    },
    summaryComparison: {
      loading: 'Cargando la comparación de resúmenes...',
      returnToSession: 'Volver a tu sesión',
      defaultTemplateTooltip:
        'Esta es tu plantilla predeterminada. Puedes cambiarla en tu configuración.',
      select: 'Seleccionar',
      generating: 'Generando...',
      generateSummaries: 'Generar resúmenes',
      inactiveTemplate: 'Plantilla inactiva',
      useThisSummary: 'Usar este resumen',
      alreadySigned: 'Esta nota ya está firmada',
      alreadyCurrent: 'Esta nota ya está establecida como el resumen actual',
    },
    rerunTranscription: {
      rerun: 'Volver a ejecutar la transcripción del archivo de audio',
      inProgress: 'Transcribiendo el archivo de audio',
    },
    manualCharting: {
      automateNotesTitle: '¿Prefieres automatizar tus notas?',
      automateNotesDescription:
        'Puedes comenzar a grabar, pero se sobrescribirán tus notas manuales existentes.',
      startRecording: 'Iniciar grabación',
    },
    controls: {
      resumeSession: 'Reanudar sesión',
      generateNote: 'Generar nota',
      pause: 'Pausar',
      resume: 'Reanudar',
      processing: 'Procesando',
      anotherAppointmentInSession: 'Otra cita está en sesión',
    },
    clientRecap: {
      emptySummary: 'El resumen para el cliente está vacío',
      emailSubject: 'Seguimiento de la sesión de hoy ({date})',
      copiedToClipboard:
        'El cuerpo del correo se copió al portapapeles; pégalo en tu correo',
      sendMailTooltip: 'Enviar correo al paciente',
    },
    sampleTranscript: {
      addFailed: 'No se pudo agregar la transcripción de ejemplo.',
      prompt:
        '¿No puedes grabar una cita ahora? ¡Prueba una transcripción de ejemplo para ver cómo funciona Eluve!',
      tryButton: 'Probar una transcripción',
    },
    superbill: {
      title: 'Superbill',
      viewPdf: 'Ver PDF',
    },
    uploadAudio: {
      unsupportedFormat: 'Formato de archivo no compatible',
      fileSystemUnavailable: 'El sistema de archivos no está disponible',
      uploadFailed: 'No se pudo subir el archivo',
      uploadFile: 'Subir archivo',
      uploadingRecording: 'Subiendo la grabación',
    },
    versionHistory: {
      title: 'Historial de versiones',
      startedSession: 'inició la sesión',
      endedSession: 'finalizó la sesión',
      editedChart: 'editó el expediente',
      signedChart: 'firmó el expediente',
      amended: 'enmendó',
      signedWithAmendments: 'firmó con enmiendas',
      lastEditedChart: 'editó el expediente por última vez',
      generatedSuperbill: 'generó el superbill',
      updatedSuperbill: 'actualizó el superbill',
    },
    lastSaved: {
      lastAmendedBy: 'Última enmienda por {name} el {time}',
      lastSignedBy: 'Última firma por {name} el {time}',
      lastSavedAt: 'Último guardado el {time}',
      saving: 'Guardando...',
    },
    overwriteSummary: {
      title: '¿Sobrescribir la nota existente?',
      description:
        'Si generas una nueva nota, tu nota existente se sobrescribirá. ¿Está bien?',
      no: 'No',
      confirm: 'Sí, continuar con la nueva nota',
    },
    insights: {
      reasonForVisitTab: 'Razón de la visita',
      considerationsTab: 'Consideraciones',
      generateFailed:
        'No pudimos generar tus insights. ¡Inténtalo de nuevo más tarde!',
      generateOutputFailed:
        'Error al generar el resultado. Inténtalo de nuevo más tarde.',
      eluveInsights: 'Eluve Insights',
      tagline:
        'Mejora la precisión diagnóstica y los resultados de los pacientes con insights impulsados por IA.',
      run: 'Ejecutar Insights',
      rerun: 'Volver a ejecutar Insights',
      expand: 'Ampliar a pantalla completa',
      collapse: 'Salir de pantalla completa',
      noInsightsTitle: 'Aún no hay insights',
      noInsightsDescription:
        'No pudimos generar insights con la información disponible actualmente. Inténtalo de nuevo cuando la conversación sea más larga.',
    },
    redFlags: {
      wasHelpful: '¿Te resultó útil este insight?',
      noneTitle: 'No se identificaron señales de alerta',
      noneDescription:
        'Revisa la pestaña de diagnóstico diferencial para ver cómo se evaluaron los indicadores clave según la conversación y la evidencia.',
      viewDdx: 'Ver DDx',
    },
    waveform: {
      muted: 'Silenciado',
    },
  },
  home: {
    toasts: {
      failedToRetrieveAppointments: 'No se pudieron obtener las citas',
    },
    calendarSidebar: {
      title: 'Calendario',
    },
    snippets: {
      title: 'Fragmentos',
      descriptionPrefix:
        'Crea texto reutilizable para agilizar tu documentación. Para insertar un fragmento, escribe',
      descriptionSuffix: 'en el editor y selecciona tu atajo.',
      countLabelOne: '{count} fragmento',
      countLabelMany: '{count} fragmentos',
      actionsAriaLabel: 'Acciones de fragmentos',
      export: 'Exportar',
      import: 'Importar',
      createNew: 'Crear nuevo',
      loading: 'Cargando ...',
      noSnippets: 'No hay fragmentos configurados',
      editSnippet: 'Editar {title}',
      deleteSnippet: 'Eliminar {title}',
      importSuccess: 'Se importaron {count} fragmentos correctamente',
      importParseError: 'Error al procesar el archivo de fragmentos',
    },
    transcriptionConfidence: {
      tooFarTitle: '¿Estás demasiado lejos de tu dispositivo?',
      tooFarDescription:
        'Intenta acercar tu dispositivo para que podamos captar bien el audio.',
    },
    ehrImport: {
      noEhrAssociationsTitle: 'Sin asociaciones de EHR',
      noEhrAssociationsDescription:
        'Las ubicaciones no están asociadas con un EHR externo. Por favor, pide a tu administrador que asocie una ubicación con un EHR.',
      successTitle: '¡Éxito!',
      importedFrom: 'Se importaron pacientes y citas de:',
      failedImportingFrom: 'Error al importar de:',
      failedTitle: 'No se pudieron importar los datos',
      failedImportingPatientsFrom:
        'No se pudieron importar pacientes y citas de:',
      refreshAndTryAgain: 'Actualiza la página e intenta de nuevo.',
    },
    ehrOnboarding: {
      allSetTitle: '¡Todo listo!',
      allSetDescription:
        '¡Tu integración con el EHR ya está configurada! Ahora puedes ver tus próximas citas en la página de inicio.',
      done: 'Listo',
      importingTitle: 'Importando citas',
      importingDescription:
        'La primera importación tarda unos segundos, pero después sincronizaremos tu EHR automáticamente.',
      importing: 'Importando...',
      importTitle: 'Importar citas',
      importDescription:
        'Importa los datos de tu EHR para que Eluve se integre perfectamente con tu flujo de trabajo',
      startImport: 'Iniciar importación',
      dataSafety: 'Descubre cómo Eluve protege tus datos',
      extensionInstalledTitle: '¡Extensión instalada!',
      connectEhr: 'Conecta tu cuenta de EHR con Eluve',
      selectLocationPlaceholder: 'Selecciona tu ubicación de {tenantName}',
      noLocationsFound: 'No se encontraron ubicaciones',
      contactAdminForLocation:
        'Por favor, contacta al administrador de la organización para configurar una ubicación',
      next: 'Siguiente',
    },
    emptyState: {
      noUpcomingAppointments: 'No hay próximas citas',
      finishSetupDescription:
        'Termina de configurar tu integración con el EHR para sincronizar tus citas con Eluve.',
      importAppointments: 'Importar citas',
      newSession: 'Nueva sesión',
      noUpcomingNext7Days:
        'No tienes citas próximas para los siguientes 7 días. Si agregaste recientemente una cita en tu EHR y no aparece, puedes intentar actualizar.',
      refresh: 'Actualizar',
      troubleshoot: 'Solucionar problemas',
      downloadExtensionDescription:
        'No tienes citas. Descarga la extensión de Eluve para una integración perfecta.',
      installExtension: 'Instalar extensión',
      startRecordingSession: 'Iniciar una sesión de grabación',
    },
    ehrImporter: {
      installExtensionTitle: 'Instala la extensión',
      installExtensionDescription:
        'Obtén la extensión de Chrome para sincronizar fácilmente tu nota con tu EHR.',
      ehrIntegration: 'Integración con el EHR',
      almostThere:
        '¡Ya casi terminas! ¡Completa la configuración de tu integración!',
      accountsConnected: '{count} cuentas conectadas',
      problemsSyncing: 'Problemas al sincronizar con tu EHR',
      completeOnboarding: 'Completar la incorporación',
      importPatientsAppointments: 'Importar pacientes y citas',
    },
    agenda: {
      patientNotSpecified: 'Paciente no especificado',
    },
    loader: {
      loadingAppointments: 'Cargando citas ...',
    },
    tenantsList: {
      pendingApproval: 'Pendiente de aprobación',
      organization: 'Organización',
      role: 'Rol',
      memberSince: 'Miembro desde',
      createdAt: 'Creado el',
    },
  },
  tenantAdmin: {
    common: {
      save: 'Guardar',
      saveChanges: 'Guardar cambios',
      create: 'Crear',
      add: 'Agregar',
      users: 'Usuarios',
      areYouSure: '¿Estás seguro?',
      thereWasAnError: 'Ocurrió un error',
      invalidFileExtension: 'Extensión de archivo inválida.',
      actionsColumn: 'Acciones',
      detailsColumn: 'Detalles',
      nameColumn: 'Nombre',
    },
    location: {
      editName: 'Editar nombre de la ubicación',
      detailsUpdated: 'Detalles de la ubicación actualizados',
      detailsUpdateFailed:
        'No se pudieron actualizar los detalles de la ubicación',
      logoUpdated: 'Logo actualizado',
      logoUpdateFailed: 'No se pudo actualizar el logo',
      logoUploadFailed: 'No se pudo subir el logo',
      displayNameUpdated: 'Nombre para mostrar actualizado',
      displayNameUpdateFailed: 'No se pudo actualizar el nombre para mostrar',
      changeLogoTooltip: 'Haz clic para cambiar el logo',
      uploadLogoTooltip: 'Haz clic para subir un logo',
      logoAlt: 'Logo de {name}',
      uploadLogo: 'Subir logo',
      removeLogo: 'Quitar logo',
      autoCreatedBadge: 'Creada automáticamente',
      renamedBadge: 'renombrada',
      detailsTitle: 'Detalles de la ubicación',
      hideAdvancedSettings: 'Ocultar configuración avanzada',
      showAdvancedSettings: 'Mostrar configuración avanzada',
      externalEhrLabel: 'EHR externo',
      selectEhr: 'Seleccionar EHR',
      removeEhrTooltip: 'Quitar el EHR de la ubicación',
      or: 'O',
      createNewEhr: 'Crear nuevo EHR',
      externalIdLabel: 'ID externo',
      addressLabel: 'Dirección',
      phoneNumberLabel: 'Número de teléfono',
      inheritedDefaultsTitle: 'Valores predeterminados heredados',
      inheritedDefaultsDescription:
        'Se aplican a esta ubicación y las heredan las ubicaciones descendientes que no definan los suyos.',
      businessNameLabel: 'Nombre del negocio',
      posCodeLabel: 'Código de lugar de servicio (POS)',
      taxIdLabel: 'ID fiscal',
      replyToEmailLabel: 'Correo de respuesta',
      addUser: 'Agregar usuario',
      billingCodesTitle: 'Códigos de facturación',
      usersStatDescription: 'Con acceso a esta ubicación',
      appointmentsStatTitle: 'Citas',
      appointmentsStatDescription: 'Registradas en esta ubicación',
      sourceOrgIdLabel: 'ID de la organización de origen',
      sourceOrgIdHint: 'Clave de coincidencia y deduplicación — solo lectura',
      sourceNameLabel: 'Nombre de origen (OrganizationName)',
      tabDetails: 'Detalles',
      tabUserPermissions: 'Permisos de usuario',
      tabPeople: 'Personas',
      tabServices: 'Servicios',
      tabBillingCodes: 'Códigos de facturación',
      schedulingTitle: 'Programación',
      schedulingTimezoneLabel: 'Zona horaria de programación',
      openingHoursLabel: 'Horario de atención',
      schedulingUnavailable: 'Sesión FHIR no disponible.',
      schedulingLoading: 'Cargando…',
      schedulingNotMirrored:
        'Esta ubicación aún no está reflejada para la programación.',
      schedulingNoTimezone:
        'Sin una zona horaria esta clínica no se puede reservar: el horario de los profesionales no tiene una grilla donde ubicarse.',
      practiceRolesTitle: 'Roles de práctica en esta ubicación',
      practiceRolesDescription:
        'La recepción agenda las citas; los profesionales clínicos además son responsables de la documentación clínica. El valor predeterminado sigue el rol del miembro en toda la organización.',
      practiceRolesLoadFailed:
        'No se pudieron cargar los roles de práctica: la edición está deshabilitada.',
      practiceRoleDefault: 'Predeterminado',
      practiceRoleUnavailable: 'No disponible',
      practiceRoleClinician: 'Profesional clínico',
      practiceRoleFrontDesk: 'Recepción',
      practiceRoleSet: 'Rol establecido en {role}.',
      practiceRoleCleared: 'Rol eliminado.',
      practiceRoleAccessSyncFailed:
        'Sus permisos de acceso no se actualizaron: usa Volver a sincronizar políticas.',
      practiceRoleSetFailed: 'No se pudo establecer el rol: {message}',
      practiceRoleUnknownError: 'error desconocido',
      practiceRoleApiUnreachable: 'No se pudo conectar con la API',
    },
    locations: {
      title: 'Ubicaciones',
      description:
        'Las ubicaciones sincronizadas desde tu EMR aparecen abajo: renombra cualquier ubicación o cambia a la vista de árbol para gestionar la jerarquía.',
      hubDescription:
        'Tu práctica, en un solo lugar: elige una ubicación para gestionar sus detalles, su personal y su programación.',
      hubEmpty: 'Aún no hay ubicaciones.',
      addLocation: 'Agregar ubicación',
      addLocationParentLabel: 'Ubicación superior',
      addLocationParentPlaceholder: 'Selecciona la ubicación superior',
      addLocationParentSearch: 'Buscar ubicaciones',
      addLocationNameLabel: 'Nombre',
      addLocationCancel: 'Cancelar',
      addLocationSubmit: 'Agregar',
      locationCreated: "Ubicación '{name}' creada",
      locationCreateFailed: "No se pudo crear la ubicación '{name}'",
      rootFallback: 'Raíz',
      empty:
        'Aún no hay ubicaciones: cambia a la vista de árbol para agregar una.',
      locationColumn: 'Ubicación',
      sourceOrgIdColumn: 'ID de org. de origen',
      apptsColumn: 'Citas',
      openLocationAria: 'Abrir {name}',
      loadFailed: 'No se pudieron cargar las ubicaciones',
      tableView: 'Tabla',
      treeView: 'Árbol',
    },
    newEhr: {
      title: 'Crear nuevo EHR',
      vendorLabel: 'Proveedor',
      selectVendor: 'Seleccionar proveedor',
      domainLabel: 'Dominio',
      domainPlaceholder: 'Ingresa el dominio',
      secretConfiguration: 'Configuración secreta',
      plaintextConfiguration: 'Configuración en texto plano',
      enterField: 'Ingresa {field}',
      addNewEhr: 'Agregar nuevo EHR',
    },
    users: {
      inviteUser: 'Invitar usuario',
      inviteSendFailed: 'No se pudo enviar la invitación.',
      inviteNewUserTitle: 'Invitar nuevo usuario',
      userRoleLabel: 'Rol del usuario',
      roleAdmin: 'Administrador',
      roleMember: 'Miembro',
      roleDescription:
        'Los administradores pueden ver todos los pacientes y citas, además de gestionar usuarios y la configuración de la organización. Nota: puedes cambiar el rol de un usuario más adelante.',
      emailLabel: 'Correo electrónico',
      emailPlaceholder: 'juan.perez@ejemplo.com',
      firstNameLabel: 'Nombre',
      firstNamePlaceholder: 'Juan',
      lastNameLabel: 'Apellido',
      lastNamePlaceholder: 'Pérez',
      locationLabel: 'Ubicación',
      selectLocation: 'Selecciona una ubicación',
      sendInvitation: 'Enviar invitación',
      inviteResendFailed: 'No se pudo reenviar el correo de invitación.',
      inviteRevokeFailed: 'No se pudo revocar la invitación.',
      resendEmail: 'Reenviar correo',
      revokeInvite: 'Revocar invitación',
      resendConfirmation: 'Esto enviará otro correo de invitación a "{email}".',
      revokeConfirmation:
        'Esto impedirá que el usuario ({email}) acepte la invitación.',
      resendEmailAction: 'Reenviar correo',
      revokeInviteAction: 'Revocar invitación',
      excludeFailed: 'No se pudo excluir al usuario de los reportes de uso.',
      includeFailed: 'No se pudo incluir al usuario en los reportes de uso.',
      emailDescription: 'Correo: {email}',
      removeFailed: 'No se pudo eliminar al usuario de la organización.',
      roleChangeFailed: 'No se pudo cambiar el rol del usuario.',
      changeToAdmin: 'Cambiar a administrador',
      changeToMember: 'Cambiar a miembro',
      includeInUsageReports: 'Incluir en los reportes de uso',
      excludeFromUsageReports: 'Excluir de los reportes de uso',
      grantReportingAccess: 'Otorgar acceso a reportes',
      revokeReportingAccess: 'Revocar acceso a reportes',
      reportingAccessFailed:
        'No se pudo cambiar el acceso a reportes del usuario.',
      reportingAccessColumn: 'Acceso a reportes',
      reportingAccessGranted: 'Otorgado',
      reportingAccessNotGranted: 'No otorgado',
      reportingAccessAdminImplicit: 'Otorgado (administrador)',
      removeUser: 'Eliminar usuario',
      removeConfirmation:
        'Esto eliminará al usuario ({email}) de tu organización.',
      roleChangeConfirmation:
        'Esto cambiará el rol del usuario ({email}) a "{role}".',
      excludeConfirmation:
        'Este usuario se ocultará de esta lista y no contará en los reportes de uso.',
      removeUserAction: 'Eliminar usuario',
      changeRoleAction: 'Cambiar rol',
      excludeUserAction: 'Excluir usuario',
      fullNameColumn: 'Nombre completo',
      emailColumn: 'Correo',
      roleColumn: 'Rol',
      inviteStatusColumn: 'Estado de la invitación',
      statusColumn: 'Estado',
      lastSeenColumn: 'Última actividad',
      joinInviteDateColumn: 'Fecha de ingreso/invitación',
      invitePendingTag: 'Invitación pendiente',
      inviteRevokedTag: 'Invitación revocada',
      inviteExpiredTag: 'Invitación vencida',
      showExcludedUsers: 'Mostrar usuarios excluidos',
      excludedUsersTooltip:
        'Los usuarios excluidos no cuentan en los reportes de uso',
      searchPlaceholder: 'Buscar usuarios por nombre o correo...',
      membersTitle: 'Miembros',
    },
    userGroups: {
      title: 'Grupos de usuarios',
      createFailed: 'No se pudo crear el grupo de usuarios',
      createUserGroup: 'Crear grupo de usuarios',
      newUserGroupTitle: 'Nuevo grupo de usuarios',
      groupNamePlaceholder: 'Ingresa el nombre del grupo',
      removeUserFailed: 'No pudimos eliminar al usuario del grupo.',
      removeUserTooltip: 'Eliminar usuario de este grupo',
      addUserFailed: 'No pudimos agregar al usuario al grupo.',
      selectUserToAdd: 'Selecciona un usuario para agregar',
      searchUsers: 'Buscar usuarios',
      noUsersToAdd: 'No hay usuarios disponibles para agregar.',
      noUsersInGroup: 'Aún no hay usuarios en este grupo.',
    },
    codes: {
      medicalCodeConfigurationsTitle: 'Configuraciones de códigos médicos',
      bulkActions: 'Acciones masivas',
      addIndividualCodes: 'Agregar códigos individuales',
      medicalCodes: 'Códigos médicos',
      codeTypeColumn: 'Tipo de código',
      codeColumn: 'Código',
      labelColumn: 'Etiqueta',
      priceColumn: 'Precio',
      removeConfigFailed:
        'No se pudo eliminar la configuración del grupo de usuarios',
      removeCodeTooltip: 'Eliminar código de este grupo',
      configCreated: 'Configuración del grupo de usuarios creada correctamente',
      configCreateFailed:
        'No se pudo crear la configuración del grupo de usuarios',
      newConfigTitle: 'Nueva configuración de código de facturación',
      customLabel: 'Etiqueta personalizada',
      customLabelDescription:
        'Si no se proporciona, se usa la etiqueta existente.',
      priceRequiredLabel: 'Precio (obligatorio)',
      modifiersLabel: 'Modificadores',
      modifiersDescription: 'Una lista de modificadores separados por comas.',
      downloadingTitle: 'Descargando códigos médicos',
      preparingDownload: 'Preparando tu descarga...',
      downloadComplete: '¡Descarga completa!',
      downloadFailed:
        'No se pudieron descargar los códigos médicos. Por favor, intenta de nuevo.',
      downloadAllCodes: 'Descargar todos los códigos disponibles',
      downloadEnabledCodes: 'Descargar códigos habilitados',
      uploadTitle: 'Subir códigos médicos',
      uploadInstructions:
        'Por favor, selecciona un archivo CSV con códigos médicos para subir. El archivo debe incluir las siguientes columnas:',
      uploadColumnCode: 'El código médico (obligatorio)',
      uploadColumnType:
        'Tipo de código, debe ser uno de: {types} (obligatorio)',
      uploadColumnDescription: 'Descripción del código (opcional)',
      uploadColumnPrice:
        "Precio por unidad en dólares, p. ej. '60' o '120.50' (opcional)",
      uploadColumnModifiers:
        'Lista de modificadores separados por comas (opcional)',
      uploadWarning:
        'Advertencia: subir un archivo nuevo reemplazará TODAS las configuraciones de códigos existentes de este grupo.',
      selectFile: 'Seleccionar archivo',
      uploadFileLimits: 'Tamaño máximo del archivo: 5MB, solo formato CSV',
      uploadingFile: 'Subiendo tu archivo...',
      processingCodes:
        'Procesando los códigos médicos, esto puede tardar un momento...',
      uploadComplete: '¡Carga completa!',
      successfullyProcessed: 'Procesados correctamente:',
      codesCount: '{count} códigos',
      skipped: 'Omitidos:',
      errors: 'Errores:',
      moreErrors: '...y {count} errores más',
      uploadFailed: 'No se pudieron subir los códigos médicos',
      uploadCodes: 'Subir códigos',
    },
    noteRecovery: {
      title: 'Recuperación de notas',
      description:
        'Regenera notas clínicas para citas donde la nota falló, nunca se generó o quedó atascada. Solo se muestran las citas de los últimos 7 días; las notas más antiguas no se pueden regenerar aquí. Se excluyen las citas con nota firmada; activa “Mostrar citas con notas” para incluir también las que ya tienen un borrador sin firmar.',
      regenerateNote: 'Regenerar nota',
      patientColumn: 'Paciente',
      startDateColumn: 'Fecha de inicio',
      userColumn: 'Usuario',
      statusColumn: 'Estado',
      filterByUser: 'Filtrar por usuario',
      filterByStatus: 'Filtrar por estado',
      allUsers: 'Todos los usuarios',
      allStatuses: 'Todos los estados',
      showWithNotes: 'Mostrar citas con notas',
      regenerationStarted: 'Se inició la regeneración de la nota',
      notPermitted:
        'No tienes permiso para regenerar notas de esta organización',
      alreadySigned: 'Esta cita ya tiene una nota firmada',
      regenerateFailed: 'No se pudo regenerar la nota',
      regenerateDescriptionPrefix: 'Regenera la nota clínica de',
      regenerateDescriptionSuffix:
        'usando el paquete de plantillas seleccionado.',
      templateBundle: 'Paquete de plantillas',
      selectTemplateBundle: 'Selecciona un paquete de plantillas',
      untitledBundle: 'Paquete sin título',
      noBundles:
        'No hay paquetes de plantillas habilitados disponibles para esta organización.',
      activeWarningPrefix: 'Esta cita todavía está activa. Generar una nota',
      activeWarningBold: 'completará la cita',
      activeWarningSuffix: 'y finalizará la sesión.',
      activeAcknowledge: 'Entiendo que esto completará la cita activa.',
    },
    sessionTypes: {
      title: 'Tipos de sesión',
      updated: 'Tipo de sesión actualizado',
      updateFailed: 'No se pudo actualizar el tipo de sesión',
      editTooltip: 'Editar este tipo de sesión',
      updateTitle: 'Actualizar tipo de sesión',
      isInitialVisitTypeLabel: 'Es tipo de visita inicial',
      contextColumn: 'Contexto',
      contextPlaceholder: 'Descripción',
      isInitialVisitColumn: 'Visita inicial',
    },
    general: {
      title: 'General',
      logoLabel: 'Logo',
      organizationNameLabel: 'Nombre de la organización',
      interfaceLanguageLabel: 'Idioma de la interfaz',
      interfaceLanguageDescription:
        'El idioma predeterminado de la interfaz para los usuarios de esta organización. Quienes hayan elegido su propio idioma no se ven afectados.',
      interfaceLanguageUpdateFailed:
        'No se pudo actualizar el idioma de la interfaz',
      changeInterfaceLanguageTitle: 'Cambiar idioma de la interfaz',
      changeInterfaceLanguagePrefix: 'Esto cambiará la interfaz para',
      someUsers: 'algunos usuarios',
      userCountOne: '{count} usuario',
      userCountMany: '{count} usuarios',
      changeInterfaceLanguageSuffix:
        'en esta organización, ya que los usuarios que eligieron un idioma explícitamente no se ven afectados.',
      editInterfaceLanguage: 'Editar idioma de la interfaz',
      logoUpdateFailed: 'No se pudo actualizar el logo de la organización',
      logoDeleteFailed: 'No se pudo eliminar el logo de la organización.',
      organizationLogoAlt: 'Logo de la organización',
      nameUpdateFailed: 'No se pudo actualizar el nombre de la organización',
      editNameTitle: 'Editar nombre de la organización',
      namePlaceholder: 'Ingresa el nombre de la organización',
      nameInputAria: 'Nombre de la organización',
      editName: 'Editar nombre de la organización',
    },
  },
  patients: {
    common: {
      loading: 'Cargando…',
      checkingAccess: 'Verificando acceso…',
      noFhirRecord: 'No hay un registro FHIR disponible para este paciente.',
      unknownError: 'Error desconocido',
      saving: 'Guardando…',
      saveChanges: 'Guardar cambios',
      keepEditing: 'Seguir editando',
    },
    list: {
      title: 'Pacientes',
      loadFailed: 'No se pudieron cargar los datos del paciente.',
      searchPlaceholder: 'Buscar...',
      details: 'Detalles',
      firstName: 'Nombre',
      lastName: 'Apellido',
      lastAppointment: 'Última cita',
      dateOfBirth: 'Fecha de nacimiento',
      importedFrom: 'Importado de',
      appointmentsCount: 'N.º de citas',
      copyExternalId: 'Copiar ID externo del paciente',
      includeInactive: 'Incluir inactivos/fallecidos',
    },
    tabs: {
      overview: 'Resumen',
      appointments: 'Citas',
      documents: 'Documentos',
      importedNotes: 'Notas importadas',
      artifacts: 'Artefactos',
      carePlans: 'Planes de cuidado',
      medicalProfile: 'Perfil médico',
      careTeam: 'Equipo de cuidado',
      observations: 'Observaciones',
      patientUploads: 'Archivos del paciente',
    },
    detailsPage: {
      copyExternalId: 'Copiar ID externo',
      viewOnVendor: 'Ver en {vendor}',
      dateOfBirth: 'Fecha de nacimiento:',
      age: 'Edad:',
      yearsOld: '{age} años',
      error: 'Error',
    },
    importedNotes: {
      viewPdf: 'Ver PDF',
      createdAt: 'Fecha de creación',
      actions: 'Acciones',
      searchByName: 'Buscar por nombre...',
    },
    profile: {
      allPatients: 'Todos los pacientes',
      askEluve: 'Pregúntale a Eluve',
      sectionsAriaLabel: 'Secciones del perfil del paciente',
      appointmentsLoadFailed: 'No se pudieron cargar los datos de las citas.',
      documentsLoadFailed: 'No se pudieron cargar los datos de los documentos.',
      importedNotesHeading: 'Notas importadas',
      importedNotesLoadFailed: 'No se pudieron cargar las notas importadas.',
      overviewEmptyDescription:
        'La información médica del paciente aparecerá aquí.',
    },
    rail: {
      editProfile: 'Editar perfil',
      moreActions: 'Más acciones',
      patientInformation: 'Información del paciente',
      identityDemographics: 'Identidad y demografía',
      contactRelationships: 'Contacto y relaciones',
      status: 'Estado',
      dateOfBirth: 'Fecha de nacimiento',
      dobWithAge: '{date} · {age} años',
      mrn: 'MRN',
      primaryCare: 'Atención primaria',
      lastVisit: 'Última visita',
      nextVisit: 'Próxima visita',
      legalSex: 'Sexo legal',
      genderIdentity: 'Identidad de género',
      pronouns: 'Pronombres',
      race: 'Raza',
      ethnicity: 'Etnicidad',
      preferredLanguage: 'Idioma preferido',
      maritalStatus: 'Estado civil',
      email: 'Correo electrónico',
      address: 'Dirección',
      emergencyContact: 'Contacto de emergencia',
      phone: 'Teléfono',
      phoneMobile: 'Celular',
      phoneHome: 'Casa',
      phoneWork: 'Trabajo',
      statusActive: 'Activo',
      statusDeceased: 'Fallecido',
      statusLeftPractice: 'Dejó el consultorio',
      markDeceased: 'Marcar como fallecido…',
      removeDeceased: 'Quitar el estado de fallecido…',
      markLeftPractice: 'Marcar como que dejó el consultorio…',
      markActive: 'Marcar como activo…',
      recordUnavailable:
        'El registro del paciente no está disponible o está restringido.',
      recordLoadError: 'No se pudo cargar el registro del paciente.',
    },
    editProfile: {
      description:
        'Actualiza la identidad, demografía y datos de contacto de {name}.',
      thisPatient: 'este paciente',
      updated: 'Perfil actualizado.',
      conflict:
        'Este perfil cambió desde que lo abriste. Cierra el diálogo y vuelve a abrirlo para editar la versión más reciente.',
      saveFailed: 'No se pudo guardar: {message}',
      unknownError: 'error desconocido',
      discardTitle: '¿Descartar los cambios no guardados?',
      discardDescription:
        'Tus ediciones a este perfil no se han guardado y se perderán si cierras ahora.',
      discardChanges: 'Descartar cambios',
    },
    status: {
      deceasedSetTitle: '¿Marcar a {name} como fallecido?',
      deceasedSetDescription:
        'Su perfil mostrará un estado de fallecido. Esto se puede deshacer más tarde.',
      deceasedSetConfirm: 'Marcar como fallecido',
      deceasedClearTitle: '¿Quitar el estado de fallecido de {name}?',
      deceasedClearDescription:
        'Su perfil ya no mostrará un estado de fallecido.',
      deceasedClearConfirm: 'Quitar estado',
      leftSetTitle: '¿Marcar que {name} dejó el consultorio?',
      leftSetDescription:
        'Su perfil mostrará que dejó el consultorio. Esto se puede deshacer más tarde.',
      leftSetConfirm: 'Marcar como que se fue',
      leftClearTitle: '¿Marcar a {name} como activo?',
      leftClearDescription: 'Su perfil ya no mostrará que dejó el consultorio.',
      leftClearConfirm: 'Marcar como activo',
      updated: 'Estado del paciente actualizado.',
      changedElsewhere:
        'El estado de este paciente acaba de cambiar en otro lugar. Actualizando.',
      updateFailed: 'No se pudo actualizar el estado del paciente.',
    },
    medicalProfile: {
      accessEluveAdminLabel: 'Acceso completo',
      accessEluveAdminDetail:
        'Como administrador de Eluve, puedes ver el registro FHIR de cada paciente en todas las organizaciones.',
      accessTenantAdminLabel: 'Acceso completo en esta organización',
      accessTenantAdminDetail:
        'Como administrador de la organización, puedes ver el registro FHIR de cada paciente en esta organización.',
      accessTenantMemberLabel: 'Eres médico de cabecera de este paciente',
      accessTenantMemberDetail:
        'Por eso puedes ver este registro. Solo verás registros FHIR de pacientes donde figures como médico de cabecera.',
      noFhirPatientTitle: 'No hay un paciente FHIR visible',
      noFhirPatientDescription:
        'Este paciente aún no se ha sincronizado con el almacén FHIR, o tu política de acceso lo filtra.',
    },
    careTeam: {
      unnamedPractitioner: 'Profesional sin nombre',
      generalPractitioner: 'Médico de cabecera',
      name: 'Nombre',
      role: 'Rol',
      contact: 'Contacto',
      reference: 'Referencia',
      history: 'Historial',
      assignmentHistory: 'Historial de asignaciones',
      assignmentHistoryDescription:
        'Cada cambio en los médicos de cabecera de este paciente, obtenido del historial del recurso FHIR.',
      noHistory: 'No hay historial disponible.',
      when: 'Cuándo',
      change: 'Cambio',
      gps: 'Médicos',
      version: 'Versión',
      unknownTime: 'Hora desconocida',
      noGpChanges: 'Sin cambios de médico de cabecera',
      initialVersion: 'Versión inicial',
      noMembers:
        'No hay miembros del equipo de cuidado asignados a este paciente.',
    },
    observations: {
      showTrends: 'Mostrar tendencias',
      loadingTrends: 'Cargando tendencias…',
      noneToChart:
        'Aún no hay observaciones para graficar. Primero extrae un informe de laboratorio desde la pestaña Archivos del paciente.',
      analyte: 'Analito',
      undated: 'Sin fecha',
      reportFallback: 'Informe',
    },
    extracted: {
      loading: 'Cargando observaciones extraídas…',
      empty:
        'Aún no hay observaciones extraídas por el agente. Sube un documento y ejecuta el agente de extracción desde la pestaña Archivos del paciente.',
      unnamedObservation: 'Observación sin nombre',
      laboratoryReport: 'Informe de laboratorio',
      labReportKind: 'Informe de laboratorio',
      extractedRecordKind: 'Registro extraído',
      reportDate: 'Informe {date}',
      extractedDate: 'Extraído {date}',
      observationCountOne: '{count} observación',
      observationCountMany: '{count} observaciones',
      needsReview: 'Requiere revisión',
      approved: 'Aprobado',
      review: 'Revisar',
      edit: 'Editar',
      reject: 'Rechazar',
      approve: 'Aprobar',
      saveAndApprove: 'Guardar y aprobar',
      collapseReport: 'Contraer informe',
      expandReport: 'Expandir informe',
      observationName: 'Nombre de la observación',
      valuePlaceholder: 'valor',
      unitPlaceholder: 'unidad',
      referenceRange: 'Rango de referencia',
      refPrefix: 'Ref.: {range}',
      approvedToastOne: 'Se aprobó {title} ({count} observación)',
      approvedToastMany: 'Se aprobó {title} ({count} observaciones)',
      rejectedToast: 'Se rechazó {title}',
      approvalFailed: 'La aprobación falló: {error}',
      rejectionFailed: 'El rechazo falló: {error}',
      observationDetails: 'Detalles de la observación',
      provenance: 'Procedencia',
      chainOfEffects: 'Cadena de efectos',
      onBehalfOf: 'En nombre de',
      agent: 'Agente',
      permissions: 'Permisos',
      issued: 'Emitido',
      source: 'Fuente',
      observation: 'Observación',
      byPublisher: 'por {publisher}',
      agentActor: '{agent} (agente)',
      unknownActor: 'desconocido',
    },
    files: {
      untitled: 'Sin título',
      loadFailed: 'No se pudieron cargar los archivos',
      uploaded: 'Se subió {name}',
      uploadFailed: 'No se pudo subir {name}',
      extractedPartialOne:
        'Se extrajeron {extracted} de {total} resultados de laboratorio; {dropped} no se pudo estructurar y se omitió. Revísalos en Perfil médico → Observaciones.',
      extractedPartialMany:
        'Se extrajeron {extracted} de {total} resultados de laboratorio; {dropped} no se pudieron estructurar y se omitieron. Revísalos en Perfil médico → Observaciones.',
      extractedSuccessOne:
        'Se extrajo {count} resultado de laboratorio. Revísalo en Perfil médico → Observaciones.',
      extractedSuccessMany:
        'Se extrajeron {count} resultados de laboratorio. Revísalos en Perfil médico → Observaciones.',
      notLabReport:
        'Este documento no es un informe de laboratorio; no se extrajo nada.',
      noLabResults:
        'No se encontraron resultados de laboratorio en este documento.',
      reextractionDisabled:
        'Este informe ya fue revisado y finalizado; la nueva extracción está deshabilitada.',
      extractionAlreadyRunning:
        'Ya hay una extracción en curso para este documento. Espera a que termine.',
      extractionFailedWithError: 'La extracción falló: {error}',
      extractionFailed: 'La extracción falló',
      installFailedWithError: 'La instalación falló: {error}',
      installFailed: 'La instalación falló',
      noDownloadUrl: 'No hay URL de descarga para este archivo.',
      openFailed: 'No se pudo abrir {name}',
      name: 'Nombre',
      type: 'Tipo',
      size: 'Tamaño',
      uploadedColumn: 'Subido',
      status: 'Estado',
      notExtracted: 'No extraído',
      viewDetails: 'Ver detalles',
      extract: 'Extraer',
      uploadFile: 'Subir archivo',
    },
    consent: {
      aiAgent: 'Agente de IA',
      unknownPublisher: 'Editor desconocido',
      allowTitle: '¿Permitir {name}?',
      builtByEluve: 'Creado por Eluve',
      builtBy: 'Creado por {publisher}',
      thirdPartyApp: 'Aplicación de terceros',
      description:
        'Este agente actúa en tu nombre y está restringido solo a las acciones listadas abajo. Nunca puede leer ni escribir nada más.',
      agentCan: 'Este agente puede:',
      allow: 'Permitir',
      publisherLogoAlt: 'Logo de {publisher}',
    },
    reviewDoc: {
      title: 'Revisar resultados extraídos',
      loadingDocument: 'Cargando documento…',
      displayError: 'No se pudo mostrar este documento.',
      documentFallback: 'Documento',
    },
    carePlans: {
      plan: 'Plan',
      status: 'Estado',
      started: 'Inicio',
      noFhirForList:
        'No hay un registro FHIR visible para este paciente, por lo que no se pueden mostrar planes de cuidado.',
      loading: 'Cargando planes de cuidado…',
      loadError:
        'Algo salió mal al cargar los planes de cuidado de este paciente.',
      empty: 'No hay planes de cuidado para este paciente.',
      backToList: '← Volver a los planes de cuidado',
      noFhirForDetail:
        'No hay un registro FHIR visible para este paciente, por lo que este plan de cuidado no se puede mostrar.',
      loadingOne: 'Cargando plan de cuidado…',
      detailLoadError: 'No se pudo cargar este plan de cuidado.',
      unknownStart: 'Inicio desconocido',
      ongoing: 'en curso',
      protocol: 'Protocolo',
      conditions: 'Condiciones',
      activities: 'Actividades',
      noActivities: 'No hay actividades registradas en este plan de cuidado.',
    },
    documents: {
      generatingPdf: 'Generando PDF...',
      pdfPreviewFailedToast: 'No se pudo generar la vista previa del PDF',
      pdfPreviewFailedMessage: 'No se pudo generar la vista previa del PDF.',
      titleEmpty: 'El título no puede estar vacío.',
      contentEmpty: 'El contenido del documento no puede estar vacío.',
      saveFailed: 'No se pudieron guardar los cambios',
      documentTitle: 'Título del documento',
      markdownTab: 'Markdown',
      pdfTab: 'PDF',
      discardTitle: '¿Descartar las ediciones no guardadas?',
      discardDescription: 'Tus cambios se perderán.',
      discard: 'Descartar',
      updated: 'Documento actualizado',
      title: 'Título',
      type: 'Tipo',
      date: 'Fecha',
    },
    conditions: {
      header: 'Condiciones',
    },
    labResults: {
      header: 'Resultados de laboratorio',
      name: 'Nombre',
      value: 'Valor',
      referenceRange: 'Rango de referencia',
      observationsCount: 'Cantidad de observaciones',
      outsideReferenceRange: 'Fuera del rango de referencia',
      issuedAt: 'Fecha de emisión',
    },
    overview: {
      reasonForVisit: 'Motivo de la visita',
      sourceFallback: 'Fuente',
      situation: 'Situación',
    },
  },
  appointmentTools: {
    common: {
      close: 'Cerrar',
      tryAgain: 'Intentar de nuevo',
      searching: 'Buscando...',
      noResultsFor: 'Sin resultados para "{query}"',
      loading: 'Cargando...',
      generatePdfFailed: 'No se pudo generar el PDF',
      preparePdfFailed: 'No se pudo preparar el PDF',
      acceptRecommendationFailed: 'No se pudo aceptar la recomendación',
    },
    regenerate: {
      noModelsFound: 'No se encontraron modelos',
      defaultFromTemplate: 'Predeterminado (de la plantilla)',
    },
    citations: {
      unknownTest: 'Prueba desconocida',
      normal: 'Normal',
      abnormal: 'Anormal',
      labResults: 'Resultados de laboratorio',
      testsCount: '{count} pruebas',
      abnormalCount: '{count} anormales',
      normalCount: '{count} normales',
      abnormalResults: 'RESULTADOS ANORMALES',
      normalResults: 'RESULTADOS NORMALES',
      refRange: '(Ref: {range})',
      result: 'RESULTADO',
      referenceRange: 'RANGO DE REFERENCIA',
      valueOutsideRange: 'El valor está fuera del rango de referencia',
      unknownMedication: 'Medicamento desconocido',
      dosageInstructions: 'INSTRUCCIONES DE DOSIFICACIÓN',
      intent: 'INTENCIÓN',
      priority: 'PRIORIDAD',
      unknownCondition: 'Condición desconocida',
      codeLabel: 'Código: {code}',
      clinicalStatus: 'ESTADO CLÍNICO',
      verification: 'VERIFICACIÓN',
      category: 'CATEGORÍA',
      note: 'NOTA',
      patientFallback: 'Paciente',
      dateOfBirth: 'Fecha de nacimiento',
      sex: 'Sexo',
      email: 'Correo electrónico',
      phone: 'Teléfono',
      address: 'Dirección',
      unknownObservation: 'Observación desconocida',
      value: 'VALOR',
      carePlan: 'Plan de cuidado',
      description: 'DESCRIPCIÓN',
      goals: 'OBJETIVOS',
      activities: 'ACTIVIDADES',
      clinicalNote: 'Nota clínica',
      appointmentLabel: 'Cita: {id}',
      noContent: 'No hay contenido disponible',
      uploadedFile: 'Archivo subido',
      pageSingle: 'Página {page}',
      pageRange: 'Páginas {start}-{end}',
      extractedText: 'TEXTO EXTRAÍDO',
      intakeForm: 'Formulario de admisión',
      patientLabel: 'Paciente: {name}',
      noFormData: 'No hay datos del formulario disponibles',
      noData: 'No hay datos disponibles',
      sourceFallback: 'Fuente',
      unnamedArtifact: 'Artefacto sin nombre',
      loadingArtifact: 'Cargando artefacto...',
      errorLoadingArtifacts: 'Error al cargar los artefactos: {message}',
      sourceNotFound:
        'No se pudo encontrar el documento fuente que contiene esta cita.',
      fetchedArtifacts: 'Se obtuvieron {count} artefacto(s).',
      sources: 'Fuentes:',
    },
    documents: {
      createTemplateTitle: 'Crear plantilla',
      editTemplateTitle: 'Editar plantilla',
      createDocumentTitle: 'Crea un documento o una carta',
      createDocumentSubtitle:
        'Empieza desde una de tus plantillas o crea una nueva. Eluve la completa a partir de esta nota: guárdala, envíala por correo o imprímela.',
      generateWithAi: 'Generar con IA',
      generateWithAiHint:
        'Describe el documento que quieres; por ejemplo, “una carta de derivación a un cardiólogo mencionando el dolor de pecho y los antecedentes del paciente”.',
      promptPlaceholder: 'Describe el documento…',
      generating: 'Generando…',
      generate: 'Generar',
      templates: 'Plantillas',
      searchTemplates: 'Buscar plantillas…',
      createNewTemplate: 'Crear una nueva plantilla',
      createNewTemplateHint:
        'Descríbela con IA para generar documentos a partir de tus notas',
      noTemplates: 'No hay plantillas disponibles',
      createdByYou: 'Creadas por ti',
      createdByEluve: 'Creadas por Eluve',
      prebuilt: 'Prediseñada',
      fillPdfTitle: 'Completa un PDF',
      fillPdfSubtitle:
        'Elige una plantilla de PDF rellenable o sube una nueva. Eluve completa los campos a partir de esta nota.',
      createPdfTemplate: 'Crear una nueva plantilla de formulario PDF',
      createPdfTemplateHint:
        'Suelta cualquier PDF estático; Eluve detecta o agrega los campos rellenables',
      noPdfTemplates: 'Aún no hay plantillas de PDF',
      yourPdfTemplates: 'Tus plantillas de PDF',
      eluveTemplates: 'Plantillas de Eluve',
      dropPdfHere: 'Suelta tu PDF aquí',
      clickToBrowse: 'Haz clic para buscar, o arrastra y suelta tu PDF aquí',
      pdfOnlyMax: 'Solo PDF, máx. 10 MB',
      enterTemplateName: 'Ingresa un nombre para la plantilla',
      enterTemplateContent: 'Ingresa el contenido de la plantilla',
      templateUpdated: 'Plantilla actualizada',
      templateCreated: 'Plantilla creada',
      updateTemplateFailed: 'No se pudo actualizar la plantilla',
      createTemplateFailed: 'No se pudo crear la plantilla',
      onlyPdfAllowed: 'Solo se permiten archivos PDF',
      fileTooLarge: 'El archivo debe pesar menos de 10 MB',
      unableToDetermineTenant: 'No se pudo determinar la organización',
      somethingWentWrong: 'Algo salió mal',
      templateGenerationFailed:
        'Falló la generación de la plantilla: {message}',
      backToTemplates: 'Volver a las plantillas',
      createTemplateSubtitle:
        'Genérala a partir de una instrucción de IA: describe el documento que esta plantilla debe producir. Por ejemplo, “una carta de derivación a un cardiólogo mencionando el dolor de pecho y los antecedentes del paciente”.',
      renamePdfTemplate:
        'Cambia el nombre de esta plantilla de formulario PDF.',
      updateTemplateContent:
        'Actualiza el contenido y el nombre de tu plantilla.',
      and: 'Y',
      addExample: 'Agrega un ejemplo como punto de partida (opcional)',
      addExampleHint:
        'Sube un documento de ejemplo; Eluve lo convierte en contenido de plantilla y lo agrega a lo que ya escribiste.',
      uploadingFile: 'Subiendo {fileName}...',
      fileFallback: 'archivo',
      generatingFromDocument: 'Generando plantilla a partir del documento...',
      templateName: 'Nombre de la plantilla',
      required: 'Obligatorio',
      namePlaceholder: 'Ponle nombre a tu plantilla',
      saving: 'Guardando...',
      save: 'Guardar',
      saveTemplateFailed: 'No se pudo guardar la plantilla',
      readingLayout:
        'Eluve está leyendo el diseño y agregando campos de formulario…',
      detectingLabels: 'Detectando etiquetas, casillas y áreas de firma',
      tryAnotherFile: 'Probar con otro archivo',
      back: 'Volver',
      createPdfTemplateTitle: 'Crear una plantilla de formulario PDF',
      createPdfTemplateSubtitle:
        'Sube un PDF estático. Eluve revisa si tiene campos rellenables y los agrega con IA si hace falta.',
      fieldsCountOne: '{count} campo',
      fieldsCountMany: '{count} campos',
      formFieldsCountOne: '{count} campo de formulario',
      formFieldsCountMany: '{count} campos de formulario',
      alreadyFillableForm:
        '{fileName} ya es un formulario rellenable · {fields}',
      addedFields:
        'Eluve agregó {fields} a {fileName}. Revísalo y luego guárdalo como plantilla.',
      detectedFields: 'CAMPOS DETECTADOS · {count}',
      saveAsTemplate: 'Guardar como plantilla',
      templateSaved: 'Plantilla guardada',
      templateSavedHint:
        '"{name}" ya está en tus plantillas de PDF. Complétala con esta nota ahora o en cualquier otro momento.',
      done: 'Listo',
      fillFromNote: 'Completar con esta nota →',
      fillableForm: 'Formulario rellenable',
      use: 'Usar',
      templateOptions: 'Opciones de la plantilla',
      edit: 'Editar',
      delete: 'Eliminar',
      deleteTemplateTitle: '¿Eliminar esta plantilla?',
      deleteTemplateDescription:
        'La plantilla {name} se eliminará permanentemente.',
      deleting: 'Eliminando...',
      createDocument: 'Crear documento',
      create: 'Crear',
      fillPdfMenuHint: 'Eluve completa un PDF rellenable con esta nota',
      createDocOrLetter: 'Crear un documento o una carta',
      createDocOrLetterHint:
        'Genera texto para enviar por correo, guardar o imprimir',
      templateDeleted: 'Plantilla eliminada',
      deleteTemplateFailed: 'No se pudo eliminar la plantilla',
      generateError: 'Algo salió mal al generar este documento.',
      preparingDocument: 'Preparando tu documento',
      docFromSession:
        'Este documento se generará a partir de los datos de tu sesión.',
      switchTabsHint:
        'Mientras tanto puedes cambiar de pestaña; seguiremos trabajando en segundo plano.',
      stopGenerating: 'Detener la generación',
      saveChangesFailed: 'No se pudieron guardar los cambios',
      copiedToClipboard: 'Copiado al portapapeles',
      copyFailed: 'No se pudo copiar',
      pdfDownloaded: 'PDF descargado',
      emailClipboardFallback:
        'Documento copiado al portapapeles; pégalo en tu correo',
      copied: 'Copiado',
      copy: 'Copiar',
      export: 'Exportar',
      downloadPdf: 'Descargar PDF',
      emailDocument: 'Enviar documento por correo',
      pdfLoadFailed: 'No se pudo cargar este PDF.',
      preparingPdf: 'Preparando tu PDF',
      pdfFromSession:
        'Este formulario PDF se completará con los datos de tu sesión.',
      preparingPdfButton: 'Preparando PDF…',
      fillError: 'Algo salió mal al completar este formulario.',
      prevPage: '← Anterior',
      nextPage: 'Siguiente →',
      pageOf: 'Página {page} de {total}',
      previewFailed: 'No se pudo previsualizar este PDF.',
      download: 'Descargar',
      moreFields: '+{count} más',
      skippedFieldsOne:
        '{count} campo no se pudo completar automáticamente; revísalo antes de usarlo: {fields}',
      skippedFieldsMany:
        '{count} campos no se pudieron completar automáticamente; revísalos antes de usarlos: {fields}',
      alteredFieldsOne:
        '{count} campo se ajustó para caber en el formulario (caracteres o longitud no compatibles); revisa: {fields}',
      alteredFieldsMany:
        '{count} campos se ajustaron para caber en el formulario (caracteres o longitud no compatibles); revisa: {fields}',
      appearanceFallback:
        'Este PDF se guardó con un modo de compatibilidad; algunos visores pueden mostrar los campos de forma distinta. Revísalo antes de enviarlo.',
      humanOnlyFieldsOne:
        '{count} campo se dejó intencionalmente para completar a mano (firmas, consentimiento, etc.)',
      humanOnlyFieldsMany:
        '{count} campos se dejaron intencionalmente para completar a mano (firmas, consentimiento, etc.)',
    },
    tabs: {
      note: 'Nota',
      carePlan: 'Plan de cuidado',
      deleteDocAria: 'Eliminar {title}',
      deleteDocumentTitle: '¿Eliminar documento?',
      deleteDocumentDescription:
        'Esto eliminará permanentemente “{title}”. Esta acción no se puede deshacer.',
    },
    billing: {
      addBillingCode: 'Agregar código de facturación',
      updatePriceFailed: 'No pudimos actualizar el precio.',
      revertChanges: 'Revertir cambios',
      removeCode: 'Quitar código',
      removeBillingCodeFailed: 'No pudimos quitar el código de facturación.',
      addBillingCodeFailed: 'No pudimos agregar el código de facturación.',
      noBillingCodesAdded: 'Aún no se han agregado códigos de facturación.',
      modifiersHint: 'Una lista de modificadores separados por comas.',
      editModifiers: 'Editar modificadores',
      updateModifiers: 'Actualizar modificadores',
      editPrice: 'Editar precio',
      updatePrice: 'Actualizar precio',
      billingDetails: 'Detalles de facturación',
      noIcdCodes: 'Aún no se han agregado códigos ICD arriba.',
      updateSuperbill: 'Actualizar superbill',
      generateSuperbill: 'Generar superbill',
      billingCodesHeading: 'Códigos de facturación',
      cptCopyrightLine1: 'CPT copyright 2025 American Medical Association.',
      cptCopyrightLine2: 'Todos los derechos reservados.',
      addAmendments: 'Agregar enmiendas',
      noBillingCodes: 'Sin códigos de facturación.',
      recommendedCodes: 'Códigos recomendados',
      generateRecommendedCodes: 'Generar códigos recomendados',
      generatingRecommendations: 'Generando recomendaciones...',
      generateRecommendationsFailed:
        'Error al generar recomendaciones de códigos de facturación. Inténtalo de nuevo más tarde.',
      addAmendmentFailed:
        'No se pudo agregar la enmienda de códigos de facturación',
      billingCodesFeedback:
        '¿Te resultaron útiles estos códigos de facturación?',
      deleteAmendmentFailed:
        'No se pudo eliminar la enmienda de códigos de facturación',
      updateModifiersFailed: 'No pudimos actualizar los modificadores.',
      updateQuantityFailed: 'No pudimos actualizar la cantidad.',
      selectIcdFailed: 'No pudimos seleccionar los códigos ICD.',
      deselectIcdFailed: 'No pudimos deseleccionar los códigos ICD.',
      removeCodeFailed: 'No se pudo quitar el código de facturación',
      addCodeFailed: 'No se pudo agregar el código de facturación',
      codeToRemoveNotFound:
        'No se encontró el código de facturación que se quería quitar',
      searchBillingCodes: 'Buscar códigos de facturación',
      chooseCodePlaceholder: 'Elige el código que quieres agregar',
      enterCodeOrSearchTerm: 'Ingresa un código o término de búsqueda',
      chooseDiagnosis: 'Elige un diagnóstico',
      searchDiagnosis: 'Buscar diagnóstico',
      superbill: 'Superbill',
    },
    medicine: {
      orderDetails: 'Detalles de la orden',
      doseLabel: 'Dosis *',
      dosePlaceholder: 'p. ej., 10 mg',
      frequencyLabel: 'Frecuencia *',
      selectFrequency: 'Selecciona la frecuencia',
      routeLabel: 'Vía *',
      selectRoute: 'Selecciona la vía',
      durationLabel: 'Duración',
      durationPlaceholder: 'p. ej., 30 días',
      quantityLabel: 'Cantidad a dispensar *',
      refillsLabel: 'Resurtidos permitidos',
      additionalInstructions: 'Instrucciones adicionales',
      instructionsPlaceholder:
        'p. ej., tomar con alimentos, evitar el alcohol...',
      updateOrder: 'Actualizar orden',
      addToOrders: 'Agregar a las órdenes',
      medicationOrders: 'Órdenes de medicación',
      searchMedications: 'Buscar medicamentos...',
      noMedicationsFound: 'No se encontraron medicamentos para "{query}"',
      newOrder: 'Nueva orden: {name}',
      currentOrders: 'Órdenes actuales',
      takeInstruction: 'Tomar {dose} {route}, {frequency}',
      viewPdf: 'Ver PDF',
      sendToPharmacy: 'Enviar a la farmacia',
      orderSentAt: 'Orden enviada a la farmacia a las {time}',
      ordersSentSuccess: 'Órdenes enviadas a la farmacia con éxito',
      quickActions: 'Acciones rápidas',
      emptyOrders:
        'Aún no hay órdenes de medicación. Busca medicamentos o revisa las recomendaciones de IA de arriba.',
      addOrderFailed: 'No se pudo agregar la orden de medicación',
      removeMedicationFailed: 'No se pudo quitar el medicamento',
      updateOrderFailed: 'No se pudo actualizar la orden de medicación',
      recommendationsFeedback:
        '¿Te resultaron útiles estas recomendaciones de medicación?',
      removeMedicineFailed: 'No se pudo quitar el medicamento',
      medication: 'Medicación',
      recommendedMedication: 'Medicación recomendada',
      medicationsFeedback: '¿Te resultaron útiles estos medicamentos?',
      typeMedicineName:
        'Escribe el nombre del medicamento y presiona Enter para buscar',
      noMedicinesFound: 'No se encontraron medicamentos',
      chooseMedicine: 'Elige el medicamento que quieres agregar',
      searchMedicineInventory: 'Buscar en el inventario de medicamentos',
    },
    referrals: {
      referralSent: 'Derivación enviada a {name}',
      referTo: 'Derivar a {name}',
      specialistInformation: 'Información del especialista',
      referralLetter: 'Carta de derivación',
      printPdf: 'Imprimir PDF',
      sendReferral: 'Enviar derivación',
      referToSpecialist: 'Derivar a un especialista',
      searchSpecialists: 'Buscar especialistas por nombre o especialidad...',
      moreResults: '{count} resultados más; refina tu búsqueda',
      noSpecialistsFound: 'No se encontraron especialistas para "{query}"',
    },
    outputs: {
      billingCodeRecommendations: 'Recomendaciones de códigos de facturación',
      editDiagnosis: 'Editar este diagnóstico',
      removeDiagnosis: 'Quitar este diagnóstico',
      justificationLabel: 'Justificación:',
      excerptLabel: 'Extracto:',
      urgencyHigh: 'Urgencia: alta',
      supportingEvidence: 'EVIDENCIA DE APOYO',
      differentialGap: 'BRECHA DIFERENCIAL',
      keyUncertainty: 'INCERTIDUMBRE CLAVE',
      suggestedNextSteps: 'PRÓXIMOS PASOS SUGERIDOS',
      sourcesCaption: 'FUENTES',
      insightHelpful: '¿Te resultó útil esta información?',
      analyzingDiagnosis: 'Analizando diagnóstico...',
      underConsideration: 'En consideración',
      mostLikely: 'MÁS PROBABLE',
      moderateSignal: 'SEÑAL MODERADA',
      weakSignal: 'SEÑAL DÉBIL',
      generateOutputFailed:
        'Error al generar el resultado. Inténtalo de nuevo más tarde.',
      noOutputAvailable: 'No hay resultados disponibles',
      outputProblem: 'Hubo un problema al generar este resultado',
      generatingOutput: 'Generando resultado',
      pfshTitle: 'Antecedentes personales, familiares y sociales (PFSH)',
      allergies: 'Alergias',
      medications: 'Medicamentos',
      existingConditions: 'Condiciones existentes',
      surgeries: 'Cirugías',
      socialHistory: 'Antecedentes sociales',
      familyHistory: 'Antecedentes familiares',
    },
    templateSelector: {
      loadingTemplates: 'Cargando plantillas...',
      defaultBundle: 'Predeterminada - {name}',
      selectTemplate: 'Seleccionar plantilla',
      updateBundleFailed: 'No se pudo actualizar el conjunto de plantillas',
      templateLabel: 'Plantilla',
      defaultForOrg: 'Predeterminada para tu organización',
    },
    sessionType: {
      updateFailed: 'No se pudo actualizar el tipo de sesión',
      noneAvailable: 'Ninguno disponible',
      noType: 'Sin tipo',
      searchTypes: 'Buscar tipos...',
    },
    page: {
      loadingAppointment: 'Cargando cita...',
      canceledInEhr: 'Esta cita se canceló en tu EHR.',
      viewExternalChart: 'Ver expediente externo',
    },
    transcription: {
      analyzingAudio: 'Analizando audio',
      analyzingAudioDescription:
        'Analizando el audio para mejorar la precisión. Quedan ({count}) archivos.',
    },
    chartsExport: {
      downloadFailedOne: '{count} cita no se pudo descargar',
      downloadFailedMany: '{count} citas no se pudieron descargar',
      notExportedOne: 'La siguiente {count} cita no se pudo exportar:',
      notExportedMany: 'Las siguientes {count} citas no se pudieron exportar:',
      cantExportDefault:
        'Las citas que seleccionaste no se pueden exportar. Asegúrate de que tengan resúmenes disponibles.',
      cantExportTitle: 'Las citas no se pueden exportar a PDF',
      preparingDownload: 'Preparando descarga',
      downloadingChartsCount: 'Descargando {count} expedientes',
      downloadingCharts: 'Descargando expedientes',
      downloadComplete: 'Descarga completa',
      chartsDownloadedOne: '{count} expediente descargado',
      chartsDownloadedMany: '{count} expedientes descargados',
      downloadChartsAsPdf: 'Descargar expedientes en PDF',
      noSummariesTooltip:
        'Las citas seleccionadas no tienen resúmenes disponibles',
    },
    appointmentsTable: {
      selectAllAria: 'Seleccionar todo',
      selectRowAria: 'Seleccionar fila',
      locationFilter: 'Ubicación',
      statusFilter: 'Estado',
      userFilter: 'Usuario',
      startDateFilter: 'Fecha de inicio',
      youSuffix: '{name} (Tú)',
      selectAllPrefix: 'Las',
      selectAllSuffix:
        'citas de esta página están seleccionadas. ¿Seleccionar las {count} citas filtradas?',
      allFilteredSelectedSuffix: 'citas filtradas están seleccionadas.',
      selectAll: 'Seleccionar todas',
      clearSelection: 'Borrar selección',
      selectedCount: '{count} seleccionadas',
      onlyMyAppointments: 'Solo mis citas',
    },
  },
  outputTemplates: {
    common: {
      templates: 'Plantillas',
      createTemplate: 'Crear plantilla',
      noCreatePermission:
        'No tienes permiso para crear plantillas. Por favor, contacta al administrador de tu organización.',
      backToTemplates: 'Volver a plantillas',
      saving: 'Guardando…',
      saveDraft: 'Guardar borrador',
      nameRequired: 'Primero dale un nombre a la plantilla',
      draftSaved: 'Borrador guardado',
      somethingWentWrong: 'Algo salió mal',
      generationFailed: 'Falló la generación de la plantilla: {message}',
      generating: 'Generando…',
      generate: 'Generar',
      preview: 'Vista previa',
      templateCreated: 'Plantilla creada',
      createTemplateFailed: 'No se pudo crear la plantilla',
    },
    generateWithAi: {
      title: 'Generar con IA',
      intro:
        'Cuéntanos sobre tu práctica y diseñaremos una plantilla adaptada a tu forma de trabajar.',
      noteFormatLabel: 'Formato de la nota',
      noteLengthLabel: 'Extensión de la nota',
      specialtyLabel: 'Especialidad',
      visitTypeLabel: 'Tipo de visita (opcional)',
      instructionsLabel: 'Instrucciones adicionales (opcional)',
      specialtyPlaceholder: 'Selecciona o escribe tu especialidad',
      visitTypePlaceholder: 'Selecciona o escribe un tipo de visita',
      instructionsPlaceholder:
        'Todo lo que deba influir en la nota: secciones que siempre incluyes, tono, requisitos de aseguradoras…',
      popularKeywords: 'Palabras clave populares',
      designing: 'Diseñando tu plantilla…',
      searchOrType: 'Busca o escribe el tuyo…',
      useTypedValue: 'Usar “{value}”',
      failedToGenerate: 'No se pudo generar la plantilla',
      unableToGenerate:
        'No se pudo generar una plantilla a partir de esta solicitud.',
      formatSoapExpansion: 'Subjetivo · Objetivo · Evaluación · Plan',
      formatSoapDescription:
        'La nota clínica estándar para la mayoría de las visitas médicas.',
      formatBirpExpansion: 'Conducta · Intervención · Respuesta · Plan',
      formatBirpDescription:
        'Se centra en la conducta observada y tus intervenciones; común en salud conductual.',
      formatDapExpansion: 'Datos · Evaluación · Plan',
      formatDapDescription:
        'Una nota simplificada que reúne todo lo observado en una sola sección de datos.',
      formatGirpExpansion: 'Meta · Intervención · Respuesta · Plan',
      formatGirpDescription:
        'Empieza por las metas del plan de tratamiento; ideal para programas de terapia orientados a objetivos.',
      lengthConciseLabel: 'Concisa',
      lengthConciseDescription:
        'Solo secciones esenciales, con indicaciones breves en cada una.',
      lengthBalancedLabel: 'Equilibrada',
      lengthBalancedDescription:
        'Secciones esenciales con estructura moderada en cada una.',
      lengthDetailedLabel: 'Detallada',
      lengthDetailedDescription:
        'Secciones esenciales con indicaciones exhaustivas y específicas de la especialidad en cada una.',
    },
    workspace: {
      previewEmptyState: 'Mira cómo se vería una nota con esta plantilla',
      generatePreview: 'Generar vista previa',
      templateNameLabel: 'Nombre de la plantilla',
      templateNamePlaceholder: 'p. ej., Nota SOAP de medicina interna',
      addSection: 'Agregar sección',
      addGroup: 'Agregar grupo',
      additionalSettings: 'Configuración adicional',
      templateDescriptionLabel: 'Descripción de la plantilla',
      templateDescriptionPlaceholder:
        'Describe cuándo debería usarse esta plantilla…',
      allowGroups: 'Permitir grupos',
      allowGroupsDescription:
        'Agrupa secciones relacionadas bajo un nombre común. Un grupo se reordena como una unidad, y las secciones se pueden arrastrar dentro y fuera de él.',
      customTranscriptOption: 'Transcripción personalizada…',
      editCustomTranscript: 'Editar transcripción personalizada',
      generatePreviewTooltip: 'Generar vista previa',
      regeneratePreviewTooltip: 'Regenerar vista previa',
      generatePreviewFailed: 'No se pudo generar la vista previa',
      customTranscriptTitle: 'Transcripción personalizada',
      customTranscriptDescription:
        'Pega la transcripción de una visita para probar esta plantilla.',
      customTranscriptPlaceholder: 'Pega la transcripción aquí…',
      useTranscript: 'Usar transcripción',
      modelLabel: 'Modelo',
      generatedIn: 'Generado en {duration}',
      tokenBreakdown: '{input} de entrada · {output} de salida',
      tokenCount: '{count} tokens',
      editSection: 'Editar sección',
    },
    wizard: {
      title: 'Crear una plantilla',
      subtitle: '¿Cómo quieres empezar?',
      aiModeDescription:
        'Describe la nota que quieres y redactaremos la plantilla.',
      uploadModeTitle: 'Sube o importa una nota existente',
      uploadModeDescription:
        'Sube o pega una nota anterior y replicaremos su estructura.',
      manualModeTitle: 'Empezar desde cero',
      manualModeDescription: 'Construye tu plantilla sección por sección.',
      orStartFromScratch: 'o empieza desde cero',
      back: 'Atrás',
      uploadStepDescription:
        'Sube una nota anterior, o pégala, y replicaremos su estructura para construir tu plantilla.',
      onlyPdfSupported: 'Solo se admiten archivos PDF.',
      fileTooLarge: 'El archivo debe pesar menos de 10 MB.',
      prepareUploadFailed: 'No se pudo preparar la subida del archivo',
      uploadFailed: 'No se pudo subir el archivo',
      failedToGenerateFromNote:
        'No se pudo generar una plantilla a partir de esta nota',
      unableToGenerateFromNote:
        'No se pudo generar una plantilla a partir de esta nota.',
      uploadingFile: 'Subiendo {fileName}…',
      fileFallback: 'archivo',
      analyzingNote: 'Analizando tu nota y redactando la plantilla…',
      dropZoneHint: 'Arrastra y suelta un PDF aquí, o haz clic para buscar',
      dropZoneLimit: 'Solo PDF, máx. 10 MB',
      removeFile: 'Quitar archivo',
      orDivider: 'O',
      pasteNotePlaceholder: 'Pega el texto completo de una nota existente…',
      removeFileToPaste:
        'Quita el archivo subido para pegar una nota en su lugar.',
    },
    edit: {
      title: 'Editar plantilla',
      disabledForOrg: 'Desactivada para la organización',
      enabledForOrg: 'Activada para la organización',
      updateAvailabilityFailed: 'No se pudo actualizar la disponibilidad',
      versionPublished: 'Versión publicada',
      newerVersionActive: 'Ya hay una versión más reciente activa',
      saveFailed: 'No se pudo guardar',
      savedCopy: 'Copia guardada',
      saveCopyFailed: 'No se pudo guardar una copia',
      copyOf: 'Copia de {name}',
      untitled: 'Sin título',
      versionNowActive: 'La versión {number} ahora está activa',
      setActiveVersionFailed: 'No se pudo establecer la versión activa',
      moreActions: 'Más acciones',
      setAsActive: 'Establecer como activa',
      enabledForOrganizationItem: 'Activada para la organización',
      saveACopy: 'Guardar una copia',
      templateVersionAria: 'Versión de la plantilla',
      versionItemActive: 'Versión {number} · Activa',
      versionItemDraft: 'Versión {number} · Borrador',
      versionItemInactive: 'Versión {number} · Inactiva',
      publishVersion: 'Publicar versión',
      editDraft: 'Editar borrador',
      creating: 'Creando…',
      createDraft: 'Crear borrador',
      templateNotFound: 'Plantilla no encontrada.',
      unsupportedBlocks:
        'Esta plantilla usa bloques avanzados que este editor aún no puede representar, así que guardar está deshabilitado: volver a guardarla aquí eliminaría esos bloques.',
      readOnlyEluvePrefix:
        'Esta plantilla la proporciona Eluve y no se puede modificar aquí. Explórala y ajústala con libertad; usa ',
      readOnlyMemberPrefix:
        'Esta plantilla pertenece a otro miembro, así que no se puede modificar aquí. Usa ',
      saveACopyInline: 'Guardar una copia',
      readOnlySuffix:
        ' para crear tu propia versión editable para esta organización.',
      adminEditingOthers:
        'Estás editando una plantilla que pertenece a otro miembro. Como administrador puedes publicar cambios; la plantilla seguirá siendo suya.',
    },
    new: {
      title: 'Nueva plantilla',
      saveDraft: 'Guardar borrador',
      saveTemplate: 'Guardar plantilla',
      templateSaved: 'Plantilla guardada',
      saveFailed: 'No se pudo guardar la plantilla',
    },
    details: {
      setDefaultFailed:
        'No se pudo establecer la variante como predeterminada.',
      updateFailed: 'No se pudo actualizar la plantilla.',
      updateNotesFailed: 'No se pudieron actualizar las notas de la variante.',
      variantJsonCopied: 'JSON de la variante copiado al portapapeles.',
      variantJsonCopyFailed: 'No se pudo copiar el JSON de la variante.',
      jsonSchemaCopied: 'JSON Schema copiado al portapapeles.',
      jsonSchemaCopyFailed: 'No se pudo copiar el JSON Schema.',
      templateNamePlaceholder: 'Nombre de la plantilla',
      templateDescriptionPlaceholder: 'Descripción de la plantilla',
      variants: 'Variantes',
      export: 'Exportar',
      variantJson: 'JSON de la variante',
      viewJsonSchema: 'Ver JSON Schema',
      jsonSchemaTitle: 'JSON Schema de la plantilla de salida',
      variantNotes: 'Notas de la variante',
      noNotes: 'Sin notas.',
    },
    bundlesList: {
      draftTooltip: 'Tienes un borrador sin publicar de esta plantilla.',
      draft: 'Borrador',
      readOnlyTooltip:
        'Proporcionada por Eluve: esta plantilla no se puede editar.',
      readOnly: 'Solo lectura',
      disabledTooltip:
        'Esta plantilla está desactivada para tu organización, así que no estará disponible al generar notas.',
      disabled: 'Desactivada',
      noDescription: 'Aún sin descripción.',
      searchPlaceholder: 'Buscar plantillas…',
      showOnlyMine: 'Mostrar solo las plantillas de mi organización',
      emptyState: 'Aún no hay plantillas: crea la primera.',
      noMatches: 'Ninguna plantilla coincide con tus filtros.',
    },
    legacyList: {
      nameHeader: 'Nombre',
      sourceHeader: 'Origen',
      descriptionHeader: 'Descripción',
      createdAtHeader: 'Fecha de creación',
      statusHeader: 'Estado',
      variantCountHeader: 'N.º de variantes',
      draft: 'Borrador',
      submitted: 'Enviada',
    },
    generateDialog: {
      title: 'Generar plantilla con IA',
      description:
        'Genera una nueva plantilla de documento médico según tus requisitos.',
      documentDescriptionRequired:
        'La descripción del documento es obligatoria',
      documentDescriptionLabel: 'Descripción del documento',
      requiredIndicator: '(Obligatorio)',
      documentDescriptionPlaceholder:
        'Describe el formato del documento médico, incluidas las secciones, los campos y los requisitos de estructura...',
      typeLabel: 'Tipo',
      typePlaceholder:
        'p. ej., "nota clínica", "informe de laboratorio", "resumen de alta"',
      requiredFieldsLabel: 'Campos obligatorios',
      requiredFieldsPlaceholder:
        'Lista separada por comas: motivo de consulta, evaluación, plan',
      constraintsLabel: 'Instrucciones adicionales',
      constraintsPlaceholder:
        'Cualquier regla de validación, requisito de formato o restricción específica...',
      exampleDocumentLabel: 'Documento de ejemplo (PDF)',
      beta: 'Beta',
      pdfDocumentAria: 'Documento PDF',
      pdfUploaded: 'PDF subido correctamente',
      uploadingPdf: 'Subiendo PDF...',
      generateTemplate: 'Generar plantilla',
      onlyPdfAllowed: 'Solo se permiten archivos PDF',
      fileTooLarge: 'El archivo debe pesar menos de 10 MB',
      invalidExtension: 'Extensión de archivo no válida',
      uploadPdfFailed: 'No se pudo subir el archivo PDF',
      pdfUploadSuccess: 'Archivo PDF subido correctamente',
      generatedSuccess: '¡Plantilla de salida generada correctamente!',
      generateFailed: 'No se pudo generar la plantilla',
      generateOutputFailed: 'No se pudo generar la plantilla de salida',
    },
    prefill: {
      title: 'Prellena tu plantilla',
      importJson: 'Importar JSON',
      pastePlaceholder: 'Pega aquí el JSON exportado',
      import: 'Importar',
      invalidJsonStructure:
        'Formato JSON no válido. Se esperaba la estructura de una plantilla exportada.',
      importSuccess: 'JSON Schema importado correctamente',
      invalidJson:
        'Formato JSON no válido. Por favor, revisa lo que ingresaste.',
    },
    preview: {
      promptTemplateRequired: 'La plantilla de prompt es obligatoria',
      invalidUuid: 'Debe ser un UUID válido',
      outputLanguageRequired: 'El idioma de salida es obligatorio',
      noTemplateAvailable:
        'No hay ninguna plantilla disponible para la generación',
      generateNoteFailed: 'No se pudo generar la nota',
      provideTranscriptBy:
        'Para generar una nota, proporciona una transcripción mediante:',
      chooseSampleTranscript: 'Elegir una transcripción de ejemplo',
      provideAppointmentId: 'Proporcionar el ID de una cita',
      appointmentIdLabel: 'ID de la cita',
      transcriptLabel: 'Transcripción',
      transcriptPlaceholder: 'Pega una transcripción de ejemplo',
      orChooseSample: 'O elige una transcripción de ejemplo:',
      promptTemplateLabel: 'Plantilla de prompt',
      outputLanguageLabel: 'Idioma de salida',
      regenerateNote: 'Regenerar nota',
      generateNote: 'Generar nota',
      hideDetails: 'Ocultar detalles',
      showDetails: 'Mostrar detalles',
      previewTemplateTab: 'Vista previa de la plantilla',
    },
    variant: {
      title: 'Crear variante de plantilla',
      createFailed: 'No se pudo crear la plantilla de salida',
    },
    headerButtons: {
      deleteDraft: 'Eliminar borrador',
      submitTemplate: 'Enviar plantilla',
    },
    unsavedChanges: {
      title: '¿Descartar los cambios sin guardar?',
      description:
        'Tienes cambios sin guardar en esta plantilla. Si sales ahora, se perderán.',
      keepEditing: 'Seguir editando',
      discard: 'Descartar cambios',
    },
    editor: {
      sectionTitlePlaceholder: 'Título de la sección',
      groupNamePlaceholder: 'Nombre del grupo',
      sectionBodyPlaceholder:
        'Describe el resultado esperado para esta sección…',
      dragToReorderSection: 'Arrastra para reordenar la sección',
      dragToReorderGroup: 'Arrastra para reordenar el grupo',
      required: 'Obligatoria',
    },
    bubbleMenu: {
      generateUsingAi: 'Generar con IA',
      writeManually: 'Escribir manualmente',
      ungroup: 'Desagrupar',
      deleteSection: 'Eliminar sección',
      deleteGroupAria: 'Eliminar el grupo y sus secciones',
      moveSectionUp: 'Mover la sección hacia arriba',
      moveSectionDown: 'Mover la sección hacia abajo',
      moveGroupUp: 'Mover el grupo hacia arriba',
      moveGroupDown: 'Mover el grupo hacia abajo',
      heading: 'Encabezado',
      subheading: 'Subencabezado',
      normalText: 'Texto normal',
      bold: 'Negrita',
      italic: 'Cursiva',
      bulletList: 'Lista con viñetas',
      numberedList: 'Lista numerada',
    },
    progress: {
      openingReadingTranscript: 'Leyendo la transcripción…',
      openingIdentifyingSpeakers: 'Identificando quién dijo qué…',
      openingGatheringFacts: 'Reuniendo datos médicamente relevantes…',
      genericCrossCheckingSymptoms:
        'Contrastando los síntomas con los antecedentes…',
      genericExtractingMedications:
        'Extrayendo nombres de medicamentos y dosis…',
      genericNotingNegatives: 'Anotando los negativos pertinentes…',
      genericFlaggingFollowUps:
        'Señalando los pendientes de seguimiento mencionados en la visita…',
      genericSeparatingDetails:
        'Separando lo que reporta el paciente de los hallazgos clínicos…',
      genericCheckingAllergies: 'Buscando alergias y contraindicaciones…',
      genericListeningForTiming: 'Detectando indicios de tiempo y duración…',
      genericMatchingComplaints:
        'Asignando las molestias a las secciones correspondientes…',
      genericReconcilingDetails:
        'Conciliando detalles mencionados más de una vez…',
      genericFilteringSmallTalk: 'Filtrando la conversación casual…',
      closingApplyingStructure: 'Aplicando la estructura de la plantilla…',
      closingTidyingFormatting: 'Puliendo el formato y la redacción…',
      closingConsistencyCheck: 'Realizando una revisión final de consistencia…',
      closingAlmostThere: 'Ya casi…',
      buildingSection: 'Construyendo la sección "{label}"…',
      pullingDetails:
        'Extrayendo detalles de la visita relevantes para {label}…',
      organizingGroup: 'Organizando las secciones de {label}…',
      listeningForPlaceholder: 'Atentos a {placeholder}…',
      fillingInPlaceholder: 'Completando {placeholder}…',
      scanningForPlaceholder: 'Buscando en la visita {placeholder}…',
    },
  },
  carePlans: {
    visitNumber: 'Visita {number}',
    phaseNumber: 'Fase {number}',
    review: {
      confirmed: 'Confirmado',
      edited: 'Editado',
      rejected: 'Rechazado',
      undo: 'Deshacer',
      edit: 'Editar',
      reject: 'Rechazar',
      delete: 'Eliminar',
      save: 'Guardar',
      saveChanges: 'Guardar cambios',
      notSet: 'Sin definir',
      confirmAllCount: 'Confirmar todos ({count})',
      allConfirmed: '✓ Todo confirmado',
      undoConfirmAllSection: 'Deshacer confirmar todos: {section}',
      loadFailed:
        'No se pudo cargar el plan de cuidados. Intenta recargar la página.',
      updateFailed: 'No se pudo actualizar el plan de cuidados.',
      planActive: 'Plan activo',
      planActiveSince: 'Plan activo desde el {date}',
    },
    planBlock: {
      title: 'Plan de cuidados',
      addresses: 'Aborda',
      goals: 'Objetivos',
      todos: 'Tareas',
      counts: '{linked} en el plan · {onRecord} solo en el expediente',
      countsLinked: '{linked} en el plan',
      addedThisVisit: 'Agregado en esta visita',
      dockThisVisit: 'Esta visita',
      addToPlan: 'Agregar {label} al plan',
      removeFromPlan: 'Quitar {label} del plan',
      emptyAddresses:
        'Nada vinculado: las afecciones confirmadas quedan en el expediente.',
      emptyGoals:
        'Nada vinculado: los objetivos confirmados quedan en el expediente.',
      emptyTodos:
        'Nada vinculado: las tareas confirmadas quedan en el expediente.',
      target: 'objetivo',
      awaitingReview: 'pendiente de revisión',
      noTargetMeasure: 'sin medida objetivo',
      targetTriggerAria: 'Elegir la medida objetivo de {label}',
      targetPickerTitle: 'Medida objetivo',
      targetPickerEmpty: 'No hay lecturas confirmadas en esta visita.',
      targetCurrentAwaiting: 'actual · pendiente de revisión',
      targetNoneAction: 'Sin medida objetivo',
    },
    netNew: {
      title: 'Nuevo en esta visita',
      countOne: '{count} nuevo',
      countMany: '{count} nuevos',
      kindCondition: 'Afección',
      kindGoal: 'Objetivo',
      kindTodo: 'Tarea',
      kindMeasure: 'Medida',
      addToPlan: 'Agregar al plan',
      addToPlanAria: 'Confirmar {label} y agregarlo al plan',
      recordOnly: 'Solo en el expediente',
      recordOnlyAria: 'Confirmar {label} solo en el expediente',
      relationAddresses: 'aborda',
      relationTracks: 'sigue',
      relationPrescribes: 'prescribe',
      targetGoal: 'Asignar a un objetivo',
      targetGoalAria: 'Elegir el objetivo que sigue {label}',
      goalPickerTitle: 'Seguir esta lectura en',
      goalPickerEmpty: 'El plan aún no tiene objetivos.',
      replaces: 'reemplaza {label}',
      noGoalRecordOnly: 'Sin objetivo, solo en el expediente',
      historyVisits: '{count} visitas',
    },
    conditions: {
      title: 'Afecciones',
      onset: 'Inicio: {date}',
      recorded: 'Registrado: {date}',
      denial: 'Negación',
      editCondition: 'Editar afección',
    },
    goals: {
      title: 'Objetivos',
      editGoal: 'Editar objetivo',
      addGoal: 'Agregar objetivo',
      deleteGoal: 'Eliminar objetivo',
      deleteConfirmTitle: '¿Eliminar este objetivo?',
      deleteConfirmBody:
        'Esto quita el objetivo del plan de cuidados y del expediente del paciente. Esto no se puede deshacer.',
      textPlaceholder: 'p. ej. Restaurar la flexión cervical a 50 grados',
      textAriaLabel: 'Texto del objetivo',
      targetDate: 'Fecha objetivo',
      targetDateAriaLabel: 'Fecha meta del objetivo',
      markDone: 'Marcar objetivo como logrado',
      markPrimary: 'Marcar como objetivo principal',
      primaryHint: 'Aparece primero; solo uno por plan',
      primary: 'Principal',
      target: 'Meta',
      status: 'Estado',
      done: 'Logrado',
    },
    measures: {
      title: 'Medidas objetivas',
      editTitle: 'Editar medida objetiva',
      addTitle: 'Agregar medida objetiva',
      addMeasure: 'Agregar medida',
      deleteMeasure: 'Eliminar medida',
      deleteConfirmTitle: '¿Eliminar esta medida?',
      deleteConfirmBody:
        'Esto quita la medida del plan de cuidados y del expediente del paciente. Esto no se puede deshacer.',
      namePlaceholder: 'Nombre de la medida',
      target: 'Meta',
      targetPlaceholder: 'p. ej. 80°',
      targetAriaLabel: 'Meta de la medida',
      targetValue: 'Meta {target}',
      thisVisitAriaLabel: '{label} en esta visita',
      trendAriaLabel: 'Tendencia de la medida',
      lastCaptured: 'Última: {value} el {date}',
      lastCapturedNoDate: 'Última: {value}',
    },
    actionItems: {
      title: 'Tareas del paciente',
      editTitle: 'Editar tarea del paciente',
      addActionItem: 'Agregar tarea del paciente',
      deleteActionItem: 'Eliminar tarea del paciente',
      deleteConfirmTitle: '¿Eliminar esta tarea del paciente?',
      deleteConfirmBody:
        'Esto quita la tarea del plan de cuidados y del expediente del paciente. Esto no se puede deshacer.',
      textPlaceholder: 'p. ej. Retracción de mentón — 3×10 al día',
      textAriaLabel: 'Texto de la tarea del paciente',
      empty:
        'Aún no hay tareas del paciente; los ejercicios aparecerán aquí cuando se prescriban.',
    },
    checklist: {
      title: 'Lista de verificación del plan de cuidados',
      tierComplete: 'Completo',
      tierGood: 'Bueno',
      tierFair: 'Regular',
      tierWeak: 'Débil',
      progress: '{done} de {total}',
      setReassessFirst:
        'Primero configura el momento de reevaluación en Calendario',
      confirmCarePlan: 'Confirmar plan de cuidados',
      confirmHint:
        'Confirma para activar este plan y mantenerlo en futuras visitas.',
    },
    phase: {
      title: 'Fase',
      planStarted: 'Plan iniciado el {date}',
      setPhaseAriaLabel: 'Establecer fase {number}',
      editAriaLabel: 'Editar fase',
    },
    schedule: {
      title: 'Calendario',
      editAriaLabel: 'Editar calendario',
      visitsCount: '{count} visitas',
      ongoing: 'Continuo',
      defaultLabel: 'predeterminada',
      earlierVisits: 'Visitas anteriores',
      laterVisits: 'Visitas posteriores',
      reassessFlag: '⚑ Reevaluar',
      visitStatusTitle: 'Visita {number} · {status}',
      statusThisVisit: 'esta visita',
      statusCompleted: 'completada',
      statusUpcoming: 'próxima',
      cadence: 'Cadencia',
      cadenceForever: '{cadence} indefinidamente',
      cadenceFor: '{cadence} por {duration} {unit}',
      cadenceEvery: '{frequency}× cada {period} {unit}',
      unitDay: 'día',
      unitDays: 'días',
      unitWk: 'sem',
      unitWks: 'sem',
      unitMo: 'mes',
      segmentFrequencyAria: 'Frecuencia del segmento {number}',
      segmentPeriodUnitAria: 'Unidad de período del segmento {number}',
      segmentDurationAria: 'Duración del segmento {number}',
      segmentDurationUnitAria: 'Unidad de duración del segmento {number}',
      removeSegmentAria: 'Quitar segmento {number}',
      perWeek: 'por semana',
      perMonth: 'por mes',
      for: 'por',
      weeks: 'semanas',
      months: 'meses',
      days: 'días',
      forever: 'indefinidamente',
      addCadence: 'Agregar cadencia',
      reassessAfter: 'Reevaluar después de',
      reassessUnitAria: 'Unidad de reevaluación',
      visitsOption: 'visitas',
      reassessRequired: 'Define una reevaluación antes de guardar.',
      proposedChange: 'Esta visita propone un calendario diferente',
      fromTranscript: 'De esta visita',
    },
    recap: {
      title: 'Resumen previo a la visita',
      goals: 'Objetivos',
      noGoals: 'Aún no hay objetivos en el plan.',
      targetDate: 'Meta {date}',
      doneLastVisit: 'Hecho en la última visita',
      noExercises: 'No hay ejercicios de la última visita.',
      addressThisVisit: 'Abordar en esta visita',
      nothingDue: 'No hay nada marcado como pendiente.',
      lastValue: 'último {value}',
    },
    callout: {
      carePlan: 'Plan de cuidados',
      dismiss: 'Descartar',
      draftedPrefix: 'Eluve redactó un',
      draftedSuffix: 'para esta visita —',
      reviewAndTailor: 'Revisar y personalizar',
      preparingPrefix: 'Eluve está preparando un',
      preparingSuffix: 'para esta visita…',
      none: 'No se recomendó ningún plan de cuidados para esta visita.',
      failed: 'Eluve no pudo preparar un plan de cuidados para esta visita.',
      notRequested:
        'No se ha generado ningún plan de cuidados para esta visita.',
      generateHint: 'Comparar esta visita con tu Biblioteca de cuidados',
      alreadyExists: 'Esta visita ya tiene un plan de cuidados',
      notCompleted:
        'Solo una visita completada puede generar un plan de cuidados',
      noMatch:
        'Ninguna plantilla de plan de cuidados coincidió con esta visita.',
      tryAgain: 'Intentar de nuevo',
      generate: 'Generar plan de cuidados',
    },
    chat: {
      thinking: 'Pensando…',
      stop: 'Detener',
      send: 'Enviar',
      draftUpdated: 'Borrador actualizado',
      valid: 'válido',
      issueCountOne: '{count} problema',
      issueCountMany: '{count} problemas',
      validatingWithMedplum: 'Validando con Medplum',
    },
    counts: {
      phaseOne: '{count} fase',
      phaseMany: '{count} fases',
      activityOne: '{count} actividad',
      activityMany: '{count} actividades',
      eventOne: '{count} evento',
      eventMany: '{count} eventos',
      bandOne: '{count} banda',
      bandMany: '{count} bandas',
    },
    tab: {
      backToNote: 'Volver a la nota',
      acceptPlan: 'Aceptar plan',
      accepted: 'Aceptado',
      recommended: 'Recomendado a partir de este encuentro',
      percentMatch: '{pct}% de coincidencia',
      phased: 'Por fases',
      weeksCount: '{count} semanas',
      startsOn: 'Comienza el {date}',
      templateLoadFailed:
        'No pudimos cargar la plantilla del plan recomendado.',
      carePlanAccepted: 'Plan de cuidados aceptado',
      acceptFailed: 'Error al aceptar: {error}',
    },
    builder: {
      title: 'Plan Builder',
      subtitle:
        'Describe un proceso de atención y guárdalo como plantilla reutilizable',
      medplumUnavailable: 'Medplum no está disponible; no se puede guardar',
      planSaved: 'Plan guardado',
      saveFailed: 'Error al guardar: {error}',
      emptyTitle: 'Describe un proceso de atención',
      emptyBody:
        'El Plan Builder lo clasifica en una forma de plan, redacta la estructura e itera contigo. Tú guardas cuando esté listo; no se almacena nada hasta entonces.',
      suggestionPhased:
        'Un protocolo de rehabilitación de LCA en 4 fases: proteger el injerto, restaurar el rango de movimiento, recuperar la fuerza y volver al deporte; cada fase con criterios antes de avanzar.',
      suggestionEpisodic:
        'Manejo de migraña crónica: una base de mantenimiento mensual, con una respuesta definida en la misma semana ante una crisis aguda y una ruta de escalamiento por sobreuso de medicación.',
      suggestionContinuous:
        'Manejo continuo de diabetes tipo 2: mantener la HbA1c entre 6 y 7.5%, revisar trimestralmente y escalar tras dos lecturas consecutivas fuera de rango.',
      attachFiles: 'Adjuntar archivos',
      composerPlaceholder:
        'Describe el proceso de atención… (Enter para enviar, Shift+Enter para salto de línea)',
      draftingPlan: 'Redactando el plan',
      resolvingIssuesOne: 'Resolviendo {count} problema de validación',
      resolvingIssuesMany: 'Resolviendo {count} problemas de validación',
      toolSearchingRecords: 'Buscando en los registros clínicos',
      toolSearchedRecords: 'Búsqueda en registros clínicos completada',
      toolSearchFailed: 'Error al buscar en los registros clínicos',
      toolReadingRecord: 'Leyendo el registro clínico',
      toolReadRecord: 'Registro clínico leído',
      toolReadFailed: 'Error al leer el registro clínico',
      toolWorking: 'Trabajando',
      toolDone: 'Listo',
      toolFailed: 'Falló',
      attachedImageAlt: 'imagen adjunta',
      removeAttachment: 'Quitar {name}',
      attachmentFallbackName: 'archivo adjunto',
      fileTypeUnsupported:
        '{name}: solo se admiten imágenes JPEG, PNG, GIF o WebP, o documentos PDF.',
      pdfTooLarge: '{name}: los PDF deben pesar 10MB o menos.',
      imageTooLarge: '{name}: las imágenes deben pesar 5MB o menos.',
      tooManyAttachments: 'Hasta {max} adjuntos por mensaje.',
      attachmentsTooLarge:
        '{name}: no hay espacio suficiente; los adjuntos deben sumar menos de 22MB.',
    },
    preview: {
      noDraftYet: 'Aún no hay borrador',
      noDraftHint:
        'Describe un proceso de atención en el chat; el borrador se mostrará aquí a medida que el Plan Builder le dé forma.',
      savePublishesPrefix: 'Guardar publica esta plantilla (estado',
      savePublishesSuffix: ').',
      saving: 'Guardando…',
      saveAsPlanDefinition: 'Guardar como Plan Definition',
      condition: 'Condición',
      shapingPlan: 'El Plan Builder está dando forma a tu plan',
      buildsLive:
        'El borrador se construye aquí en vivo mientras se transmite.',
      resolvingRedrafting:
        'Resolviendo problemas de validación; redactando de nuevo…',
      draftingStreaming: 'Redactando: el plan todavía se está transmitiendo.',
      validatingDraft: 'Validando el borrador con Medplum…',
      bannerResolvingOne:
        'Resolviendo {count} problema de validación; redactando de nuevo',
      bannerResolvingMany:
        'Resolviendo {count} problemas de validación; redactando de nuevo',
      bannerDrafting:
        'Redactando: el plan se construye en vivo mientras se transmite',
      bannerValidating: 'Borrador completo; validando con Medplum',
      validFhir: 'FHIR válido, listo para guardar.',
      validationFailedWillFix:
        'La validación falló; el Plan Builder lo corregirá y volverá a emitirlo antes de poder guardar.',
      previewFhir: 'Vista previa FHIR',
      previewFhirDescription:
        'El Bundle de transacción exacto que Guardar enviará: el PlanDefinition más sus ActivityDefinitions, compilados de forma determinista a partir del borrador actual.',
      bundleSingle: '· el PlanDefinition que Guardar creará',
      bundleMany: '· los {count} recursos que Guardar creará',
    },
    templates: {
      pageTitle: 'Plantillas de planes de cuidados',
      newPlan: 'Nuevo plan',
      allCarePlans: 'Todos los planes de cuidados',
      requiresMedplum:
        'Las plantillas de planes de cuidados requieren que Medplum esté habilitado para esta organización.',
      countOne: '{count} plantilla',
      countMany: '{count} plantillas',
      loading: 'Cargando…',
      searchPlaceholder: 'Buscar por nombre, condición o estado…',
      loadFailed:
        'No se pudieron cargar las plantillas de planes de cuidados: {error}',
      detailLoadFailed:
        'No se pudo cargar esta plantilla de plan de cuidados: {error}',
      noMatchesPrefix: 'Ninguna plantilla coincide con "',
      noMatchesSuffix: '".',
      emptyState: 'Aún no hay plantillas de planes de cuidados.',
      colTemplate: 'Plantilla',
      colConditions: 'Condiciones',
      colShape: 'Forma',
      colVersion: 'Versión',
      colStatus: 'Estado',
      fhirJson: 'FHIR JSON',
      fhirJsonDescription: 'El recurso PlanDefinition almacenado.',
      viewFhir: 'Ver FHIR',
    },
  },
  smartBlocks: {
    featureFlagged: {
      redirected: 'Te hemos redirigido',
    },
    amendment: {
      updateFailed: 'No se pudo actualizar la enmienda',
      deleteFailed: 'No se pudo eliminar la enmienda',
      duplicateFailed: 'No se pudo duplicar el contenido en la enmienda',
    },
    appointmentName: {
      updateFailed: 'No se pudo actualizar el nombre de la cita',
      updateLabel: 'Actualizar el nombre de la cita',
    },
    insightFeedback: {
      thanks: '¡Gracias por tus comentarios! Esto nos ayuda a mejorar Eluve.',
      submitFailed: 'No se pudieron enviar los comentarios',
    },
    inlineFeedback: {
      sendFailed: 'No se pudieron enviar los comentarios',
      updateFailed: 'No se pudieron actualizar los comentarios',
      updateCommentFailed: 'No se pudo actualizar el comentario',
    },
    customInstructions: {
      title: 'Mejorar notas',
      description:
        'Personaliza tus notas con indicaciones específicas. Intenta ser lo más claro y conciso posible para obtener mejores resultados.',
      placeholder: 'Escribe tus instrucciones aquí',
      examples: 'Ejemplos',
      appliesToThisNote: 'Estos cambios solo se aplicarán a esta nota.',
      regenerateNote: 'Regenerar nota',
      regenerating: 'Regenerando tu resumen',
      regenerateFailed:
        'No se pudo regenerar el resumen. Por favor, intenta de nuevo',
      chipRemoveParts: 'Elimina las partes sobre...',
      chipIncludeDiscussion: 'Incluye la conversación sobre...',
      chipMoreConcise: 'Haz las notas más concisas...',
      chipMoreDetails: 'Agrega más detalles en la sección del plan...',
    },
    rollback: {
      title: 'Deshacer cambios',
      tooltip:
        'Aún puedes volver a la versión anterior tocando el botón de deshacer.',
      failed: 'No se pudo volver al resumen anterior',
      undo: 'Deshacer',
    },
    lifecycleStatus: {
      updated: 'Estado actualizado a "{status}".',
      changedElsewhere:
        'El estado de este usuario acaba de cambiar en otro lugar. Actualizando.',
      updateFailed: 'No se pudo actualizar el estado.',
      changeStatusAria: 'Cambiar estado, actualmente {status}',
    },
    promptTemplateSelector: {
      noMatches: 'No se encontraron plantillas coincidentes.',
      loadingDefault: 'Cargando plantilla predeterminada...',
      selectTemplate: 'Selecciona una plantilla',
      searchPlaceholder: 'Buscar plantillas de prompts...',
    },
    locationSelector: {
      selectLocation: 'Selecciona una ubicación',
      searchPlaceholder: 'Buscar ubicaciones...',
      noLocation: 'Sin ubicación',
    },
    patientSelector: {
      selectPatient: 'Seleccionar paciente',
      searchPlaceholder: 'Busca por nombre, teléfono o correo',
      clearSelected: 'Quitar paciente seleccionado',
      searching: 'Buscando pacientes...',
      typeToSearch: 'Escribe al menos 2 caracteres para buscar',
      patientAdded: 'Paciente {name} agregado',
      addFailed: 'No se pudo agregar el nuevo paciente',
      createNew: 'Crear nuevo',
      dob: 'F. nac.:',
    },
    microphoneSelector: {
      ariaLabel: 'Selector de fuente de audio',
      selectMicrophone: 'Selecciona un micrófono',
    },
    micSettings: {
      audioSource: 'Fuente de audio',
    },
    tabNav: {
      more: 'Más',
      moreTabsAria: 'Más pestañas de navegación',
      selectTab: 'Selecciona una pestaña',
      tabs: 'Pestañas',
    },
    statusWidget: {
      uploading: 'Subiendo',
      offline: 'Sin conexión',
      uploadingOneFile: 'Subiendo un archivo',
      uploadingManyFiles: 'Subiendo {count} archivos',
      keepOpen:
        'Mantén Eluve abierto y tu dispositivo conectado a internet hasta que esto termine.',
      currentlyOffline: 'Actualmente estás sin conexión',
      offlineHint:
        'Si estás en sesión puedes seguir grabando, pero algunas funciones como la generación de notas podrían requerir conexión.',
    },
    goToPatient: {
      tooltip: 'Ir al paciente',
    },
    dynamicArtifact: {
      emailSubject: 'Seguimiento de la sesión de hoy ({date})',
      emailCopied:
        'Cuerpo del correo copiado al portapapeles. Pégalo en tu correo',
      sendMailTooltip: 'Enviar correo al paciente',
      addAmendments: 'Agregar enmiendas',
      valueFallback: 'Valor',
    },
    templateBuilder: {
      allBlocksMustBeValid: 'Todos los bloques deben ser válidos',
      atLeastOneBlockRequired:
        'Se requiere al menos un bloque que no sea de grupo',
      requiredField: 'Obligatorio',
      aboutThisTemplate: 'Acerca de esta plantilla',
      export: 'Exportar',
      exportTemplate: 'Exportar plantilla',
      jsonSchemaCopied: 'Esquema JSON copiado al portapapeles',
      copyJsonSchema: 'Copiar esquema JSON',
      pasteJsonSchemaPlaceholder: 'Pega el esquema JSON aquí',
      nameLabel: 'Nombre',
      typeLabel: 'Tipo',
      outputTypePlaceholder: 'Tipo de salida',
      descriptionLabel: 'Descripción',
      descriptionPlaceholder:
        'Proporciona una descripción de lo que debería ser el artefacto producido por esta plantilla.',
      variantNotes: 'Notas de la variante',
      variantNotesDescription:
        'Notas internas para esta variante de plantilla. Son visibles para los editores de plantillas y no aparecerán en el contenido generado.',
      variantNotesPlaceholder: 'Agrega notas sobre esta variante...',
      blocks: 'Bloques',
      collapseAll: 'Contraer todo',
      expandAll: 'Expandir todo',
      viewJson: 'Ver JSON',
      templateJson: 'JSON de la plantilla',
      templateJsonCopied: 'JSON de la plantilla copiado al portapapeles.',
      templateJsonCopyFailed: 'No se pudo copiar el JSON de la plantilla.',
      addBlock: 'Agregar bloque',
      groupNamePlaceholder: 'Nombre del grupo',
      textBlockNamePlaceholder: 'Nombre del bloque de texto',
      checkboxNamePlaceholder: 'Nombre de la casilla',
      expand: 'Expandir',
      collapse: 'Contraer',
      removeBlock: 'Eliminar bloque',
      confirmRemoveTitle: '¿Estás seguro?',
      confirmRemoveDescription: 'Eliminar este bloque no se puede revertir.',
      remove: 'Eliminar',
    },
    blockDetails: {
      fillOutBlockWith: 'Completar este bloque con',
      ai: 'IA',
      manual: 'Manual',
      aiOptionDescription:
        'La IA completará este bloque. El usuario puede editar el borrador de la IA.',
      manualOptionDescription:
        'Este bloque comenzará con el texto de ejemplo. El usuario lo completa.',
      required: 'Obligatorio',
      requiredDescription:
        'La IA siempre intentará completar este bloque. Nunca debería dejarlo en blanco.',
      allowEmail: 'Permitir a los usuarios enviar este bloque por correo',
      allowEmailDescription:
        'Si se habilita, los usuarios tendrán controles para enviar el contenido de este bloque por correo.',
      aiInstructions: 'Instrucciones para la IA',
      aiInstructionsPlaceholder:
        'Describe lo que la IA debería generar en este bloque. Por ejemplo: "Escribe una breve descripción del motivo de consulta del paciente. No uses oraciones completas."',
      checkboxes: 'Casillas de verificación',
      optionPlaceholder: 'Opción {number}',
      addOption: 'Agregar opción',
      placeholderTextTooltip:
        'Cuando se usa en modo de registro manual, este texto se prellenará en el cuadro de texto.',
      placeholderText: 'Texto de ejemplo',
      importedDescription: 'Descripción importada',
      importedDescriptionTooltip:
        'Esta descripción se importó desde el EHR. La descripción importada se usará como guía para la IA al generar una nota. Anúlala proporcionando tu propia descripción arriba para que la use la IA.',
    },
    blockPicker: {
      groupName: 'Grupo',
      groupTip: 'Contenedor para otros bloques',
      textName: 'Bloque de texto',
      textTip: 'Texto libre',
      checkboxName: 'Casillas de verificación',
      checkboxTip: 'Lista de opciones con casillas',
      add: 'Agregar',
    },
  },
  userSettings: {
    save: 'Guardar',
    updateProfileFailed: 'No se pudo actualizar el perfil',
    profile: {
      dialogTitle: 'Editar nombre completo',
      editFullName: 'Editar nombre completo',
      firstName: 'Nombre',
      lastName: 'Apellido',
    },
    language: {
      notesLanguage: 'Idioma de las notas',
      notesLanguageDescription: 'Idioma utilizado en la nota generada.',
      spokenLanguage: 'Idioma hablado',
      spokenLanguageDescription: 'Idioma hablado durante tus sesiones.',
    },
    providerDetails: {
      placeholder:
        'Todo lo que deba aparecer en las notas firmadas: número de licencia, NPI, DEA, etc.',
    },
    soundEffects: {
      updateFailed:
        'No se pudo actualizar la configuración de efectos de sonido. Inténtalo de nuevo.',
      label: 'Efectos de sonido',
    },
    appGrants: {
      empty: 'Aún no has instalado ningún agente.',
      granted: 'Otorgado {date}',
      unknownDate: 'desconocido',
      revoke: 'Revocar',
      revoked: 'Se revocó {agentId}',
      revokeFailed: 'No se pudo revocar',
    },
    signature: {
      invalidFileExtension: 'Extensión de archivo no válida.',
      uploadFailed: 'No pudimos subir el archivo.',
      updateFailed: 'No pudimos actualizar tu firma.',
      alt: 'Firma',
    },
    specialty: {
      updateFailed: 'No se pudo actualizar la especialidad.',
    },
    templates: {
      updateFailed: 'No se pudo actualizar la plantilla predeterminada',
      loading: 'Cargando plantillas...',
      select: 'Seleccionar plantilla',
    },
  },
  insightsSettings: {
    customCharts: {
      title: 'Historiales personalizados',
      description: 'Pega los datos del historial → Genera insights vía API',
      placeholder: 'Pega un arreglo JSON con los datos del historial',
      invalidSchema: 'Esquema no válido',
      invalidJsonSyntax: 'Sintaxis JSON no válida',
      jsonParseError: 'Error al analizar JSON: {error}',
      processFailed: 'No se pudo procesar el JSON',
      validJson: 'JSON válido, listo para generar',
      generating: 'Generando...',
      generate: 'Generar insights',
    },
  },
  careLibrary: {
    page: {
      eyebrow: 'Biblioteca',
      loadError: 'Algo salió mal al cargar la Biblioteca de cuidados.',
      medplumRequired:
        'La Biblioteca de cuidados requiere que Medplum esté habilitado para esta organización.',
      loadingOfferings: 'Cargando servicios…',
    },
    browse: {
      allTypes: 'Todos los tipos',
      offeringsCountLabel: 'servicios',
      typesCountLabel: 'tipos',
      searchPlaceholder: 'Buscar servicios…',
      searchAriaLabel: 'Buscar servicios',
      allOfferings: 'Todos los servicios',
      typeOfferings: 'Servicios de {type}',
      noMatches: 'Ningún servicio coincide con los filtros actuales.',
    },
    rail: {
      browseByCategory: 'Explorar por categoría',
      primaryFacet: 'principal',
    },
    detail: {
      backToLibrary: 'Volver a la Biblioteca de cuidados',
      loadError: 'Algo salió mal al cargar este servicio.',
      loadingOffering: 'Cargando servicio…',
      notFound: 'No se encontró ese servicio.',
      noClinicalCode: 'Sin código clínico',
      parameters: 'Parámetros',
      parametersNote: '· {type} · se prescribe por paciente al crear el plan',
      patientInstructions: 'Instrucciones para el paciente',
      categoriesTitle: 'Categorías',
      belongsToPrefix: 'Pertenece a',
      belongsToSuffix:
        'categorías — un elemento puede estar en varias a la vez.',
      usedInPlansTitle: 'Usado en planes',
      usedInPlansEmpty:
        'Los planes que hacen referencia a este servicio aparecerán aquí, cada uno con la dosis que prescribe, cuando el catálogo esté respaldado por datos de planes en vivo.',
      reuseByReference:
        'La reutilización es por referencia. Editar esta entrada se propaga automáticamente a todos los planes.',
    },
  },
  microphone: {
    noAudioDetectedCompact: 'No se detecta audio.',
    noAudioDetectedFull:
      'No se detecta audio. Por favor, selecciona otra fuente de audio para comenzar.',
    permissionsDeniedTitle: 'Permisos de micrófono denegados',
    permissionsDeniedMessage:
      'Por favor, habilita los permisos de micrófono en la configuración de tu navegador para activar la entrada de audio.',
    noDevicesTitle: 'Se requieren dispositivos de micrófono',
    noDevicesMessage:
      'No hay micrófonos conectados a tu sistema. Por favor, otorga permisos de micrófono o reintenta.',
    retryPermission: 'Reintentar permiso',
    permissionsRequiredTitle: 'Se requieren permisos de micrófono',
    permissionsRequiredMessage:
      'Por favor, otorga permisos de micrófono para activar la entrada de audio.',
    requestPermission: 'Solicitar permiso',
  },
};
